import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useOutletContext } from "react-router-dom";
import { formatKolkataDate, formatKolkataDateTime } from "../../utils/dateTime";
import {
  buildStatementSummary,
  downloadStatementCsv,
  downloadStatementExcel,
  downloadStatementPdf,
  filterStatementRows,
  formatStatementAmount,
  formatStatementDate,
  formatStatementPurpose,
  formatStatementReference,
  formatWalletName,
} from "../../utils/statementUtils";
import { buildMarksSummary, buildModuleDetailsRows, fetchAmciDesign, fetchGradePoints, printAmciDocument, resolveGradePointByPercentage, resolveStudentPhoto } from "../../utils/amciDesign";
import DirectChat from "../../components/DirectChat";
import html2pdf from "html2pdf.js";

const API = "/api";
const THUMBNAIL_ROOT = "/uploads/course-thumbnails";

const cardGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
  gap: "16px",
};

const statCard = {
  background: "linear-gradient(135deg, rgba(255,255,255,0.92) 0%, rgba(248,250,252,0.98) 100%)",
  border: "1px solid rgba(148, 163, 184, 0.16)",
  borderRadius: "22px",
  padding: "18px",
  boxShadow: "0 16px 28px rgba(15, 23, 42, 0.06)",
};

const tableWrap = { overflowX: "auto" };
const tableStyle = { width: "100%", borderCollapse: "collapse" };
const thStyle = { textAlign: "center", padding: "14px 12px", color: "#7c2d3a", borderBottom: "1px solid rgba(148, 163, 184, 0.16)", fontSize: "13px", background: "rgba(255,247,237,0.72)" };
const tdStyle = { padding: "14px 12px", borderBottom: "1px solid rgba(148, 163, 184, 0.12)", color: "#334155", fontSize: "13px", lineHeight: 1.6, fontWeight: 500 };
const uploadsBase = "/uploads";
const overlayStyle = { position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)", display: "flex", alignItems: "center", justifyContent: "center", padding: "20px", zIndex: 3000 };
const modalCardStyle = { background: "#fff", borderRadius: "22px", width: "min(760px, 100%)", maxHeight: "82vh", overflow: "auto", padding: "20px", boxShadow: "0 28px 80px rgba(15, 23, 42, 0.25)" };
const detailGridStyle = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "14px" };
const rowListWrapStyle = {
  border: "1px solid rgba(148, 163, 184, 0.18)",
  borderRadius: "18px",
  background: "#fff",
  boxShadow: "0 18px 40px rgba(15, 23, 42, 0.06)",
  overflow: "hidden",
  display: "grid",
  gap: "10px",
  padding: "10px",
};
const rowCardStyle = {
  padding: "10px 12px",
  border: "1px solid rgba(226, 232, 240, 0.95)",
  borderRadius: "14px",
  background: "linear-gradient(180deg, #ffffff 0%, #fcfdff 100%)",
  boxShadow: "0 12px 28px rgba(15, 23, 42, 0.06)",
};
const rowLabelStyle = {
  fontSize: "10px",
  lineHeight: 1.4,
  fontWeight: 700,
  color: "#64748b",
  marginBottom: "4px",
  textAlign: "center",
};
const rowValueStyle = {
  fontSize: "13px",
  lineHeight: 1.4,
  color: "#0f172a",
  minWidth: 0,
  textAlign: "center",
};
const rowMutedStyle = {
  fontSize: "11px",
  lineHeight: 1.35,
  color: "#64748b",
  textAlign: "center",
};
const rowCellStyle = {
  minWidth: 0,
  paddingRight: "10px",
  borderRight: "1px solid rgba(226, 232, 240, 0.95)",
  display: "grid",
  gridTemplateRows: "auto 1fr",
  height: "100%",
};
const rowContentStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  minHeight: 0,
  height: "100%",
};
const dividerCellStyle = {
  borderRight: "1px solid rgba(148, 163, 184, 0.16)",
};
const centerCellStyle = {
  textAlign: "center",
};
const autoWidthCellStyle = {
  width: "1%",
  whiteSpace: "nowrap",
};

function fileUrl(fileName) {
  return fileName ? `${uploadsBase}/${fileName}` : "";
}

function walletLabel(label, key) {
  if (key === "main_wallet") return "Wallet Balance";
  if (key === "gift_card_wallet") return "Gift Card";
  if (key === "reserve_wallet") return "Skill Point";
  if (key === "tools_wallet") return "Tools Point";
  return label;
}

function getErrorMessage(error, fallback) {
  const response = error?.response?.data;

  if (response?.message) {
    return response.message;
  }

  if (response?.errors) {
    const firstGroup = Object.values(response.errors)[0];
    if (Array.isArray(firstGroup) && firstGroup[0]) {
      return firstGroup[0];
    }
  }

  return fallback;
}

function groupCourseSyllabus(rows) {
  const groups = {};
  (rows || []).forEach((row) => {
    const key = Number(row.semester_no || 1);
    if (!groups[key]) {
      groups[key] = {
        semester_no: key,
        semester_title: row.semester_title || `Semester ${key}`,
        items: [],
      };
    }
    groups[key].items.push(row);
  });

  return Object.values(groups).sort((left, right) => left.semester_no - right.semester_no);
}

function semesterTotalMarks(semester) {
  return (semester?.items || []).reduce((sum, item) => sum + totalModuleMarks(item), 0);
}

function totalModuleMarks(item) {
  return Number(item?.max_marks || 0) + Number(item?.practical_max_marks || 0) + Number(item?.project_max_marks || 0) + Number(item?.viva_max_marks || 0);
}

function formatMarks(value) {
  const numericValue = Number(value || 0);
  return Number.isInteger(numericValue) ? String(numericValue) : String(numericValue);
}

function escapeHtml(value) {
  return String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function printHtmlDocument(title, body) {
  const popup = window.open("", "_blank", "width=960,height=1000");
  if (!popup) return;

  popup.document.write(`
    <html>
      <head>
        <title>${escapeHtml(title)}</title>
        <style>
          body { font-family: "Segoe UI", sans-serif; margin: 0; padding: 24px; color: #0f172a; background: #f8fafc; }
          .sheet { max-width: 920px; margin: 0 auto; background: #fff; border-radius: 24px; border: 1px solid rgba(148,163,184,.18); overflow: hidden; }
          .hero { padding: 28px 32px; color: #fff; background: linear-gradient(135deg, #4e1b24 0%, #7c2d3a 52%, #c2410c 100%); }
          .body { padding: 28px 32px; }
          .grid { display: grid; gap: 14px; }
          .two { grid-template-columns: repeat(2, minmax(0, 1fr)); }
          .card { border: 1px solid rgba(148,163,184,.18); border-radius: 18px; padding: 16px; background: #fff; }
          table { width: 100%; border-collapse: collapse; }
          th, td { border-bottom: 1px solid #e2e8f0; padding: 12px 10px; text-align: left; }
          .badge { display: inline-block; padding: 8px 12px; border-radius: 999px; background: #dcfce7; color: #166534; font-weight: 700; }
          .certificate-name { font-size: 34px; margin: 0 0 16px; color: #7c2d3a; text-align: center; }
          @media print { body { background: #fff; padding: 0; } .sheet { border: none; border-radius: 0; box-shadow: none; } }
        </style>
      </head>
      <body>${body}</body>
    </html>
  `);
  popup.document.close();
  popup.focus();
  popup.print();
}

function DetailTile({ label, value }) {
  return (
    <div style={{ background: "rgba(255,255,255,0.86)", border: "1px solid rgba(148, 163, 184, 0.14)", borderRadius: "18px", padding: "14px", boxShadow: "0 12px 22px rgba(15, 23, 42, 0.04)" }}>
      <div style={{ color: "#7c2d3a", fontSize: "12px", marginBottom: "6px", fontWeight: 700 }}>{label}</div>
      <div style={{ color: "#0f172a", fontWeight: 600, wordBreak: "break-word" }}>{value || "-"}</div>
    </div>
  );
}

function StatusChip({ status }) {
  const current = status || "pending";
  const styles = {
    approved: { color: "#166534", background: "#dcfce7" },
    hold: { color: "#92400e", background: "#fef3c7" },
    rejected: { color: "#b91c1c", background: "#fee2e2" },
    pending: { color: "#1d4ed8", background: "#dbeafe" },
  };

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "6px 10px",
        borderRadius: "999px",
        fontSize: "12px",
        fontWeight: 700,
        textTransform: "capitalize",
        ...(styles[current] || styles.pending),
      }}
    >
      {current}
    </span>
  );
}

function ApplicationPreviewModal({ item, onClose, showAllDocuments = false }) {
  if (!item) return null;

  const documentItems = [
    { label: "Photo", key: "photo", isImage: true },
    { label: "Signature", key: "signature", isImage: true },
    { label: "ID Proof", key: "id_proof", isImage: false },
    { label: "Academic Document", key: "academic_document", isImage: false },
  ];

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalCardStyle} onClick={(event) => event.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: "12px", marginBottom: "18px", flexWrap: "wrap" }}>
          <div>
            <h3 style={{ margin: "0 0 6px", color: "#0f172a" }}>Student Application View</h3>
            <p style={{ margin: 0, color: "#64748b" }}>Full student application format with details, photo, signature, and uploaded documents.</p>
          </div>
          <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
            Close
          </button>
        </div>

        <div style={detailGridStyle}>
          <DetailTile label="Application No" value={item.application_no} />
          <DetailTile label="Registration No" value={item.registration_no} />
          <DetailTile label="Student Name" value={item.student_name} />
          <DetailTile label="Study Centre" value={item.study_centre_name} />
          <DetailTile label="State" value={item.state_name} />
          <DetailTile label="District" value={item.district_name} />
          <DetailTile label="Phone" value={item.phone} />
          <DetailTile label="Email" value={item.email} />
          <DetailTile label="DOB" value={item.dob} />
          <DetailTile label="Father Name" value={item.father_name} />
          <DetailTile label="Admission Date" value={item.admission_date} />
          <DetailTile label="Course Completion" value={item.course_completion_date} />
          <DetailTile label="Guardian Name" value={item.guardian_name} />
          <DetailTile label="Session" value={item.session_year} />
          <DetailTile label="Course" value={item.course_name} />
          <DetailTile label="Status" value={item.status} />
        </div>

        <div style={{ ...statCard, marginTop: "16px" }}>
          <div style={{ color: "#64748b", fontSize: "12px", marginBottom: "6px", fontWeight: 700 }}>Address</div>
          <div style={{ color: "#0f172a", whiteSpace: "pre-wrap" }}>{item.address || "-"}</div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "16px", marginTop: "16px" }}>
          {documentItems
            .filter((documentItem) => showAllDocuments || documentItem.key === "photo" || documentItem.key === "signature")
            .map((documentItem) => (
              <div key={documentItem.key} style={{ ...statCard, textAlign: "center" }}>
                <div style={{ marginBottom: "10px", color: "#475569", fontWeight: 700 }}>{documentItem.label}</div>
                {item[documentItem.key] ? (
                  documentItem.isImage ? (
                    <img
                      src={fileUrl(item[documentItem.key])}
                      alt={documentItem.label}
                      style={{ width: "100%", maxHeight: "220px", objectFit: "contain", borderRadius: "12px", border: "1px solid #e2e8f0" }}
                    />
                  ) : (
                    <a href={fileUrl(item[documentItem.key])} target="_blank" rel="noreferrer" className="btn btn-outline-primary btn-sm">
                      Open {documentItem.label}
                    </a>
                  )
                ) : (
                  <div style={{ color: "#94a3b8" }}>Not uploaded</div>
                )}
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}

function CoursePreviewModal({ item, onClose }) {
  const [moduleRows, setModuleRows] = useState(() => (Array.isArray(item?.syllabus) ? item.syllabus : []));
  const [loadingModules, setLoadingModules] = useState(false);

  useEffect(() => {
    let active = true;
    if (!item) {
      setModuleRows([]);
      setLoadingModules(false);
      return () => {
        active = false;
      };
    }

    const existingRows = Array.isArray(item.syllabus) ? item.syllabus : [];
    setModuleRows(existingRows);

    if (existingRows.length || !item.id) {
      return () => {
        active = false;
      };
    }

    const loadModules = async () => {
      try {
        setLoadingModules(true);
        const res = await axios.get(`${API}/franchise-portal/course-modules/${item.id}`);
        if (active) {
          setModuleRows(Array.isArray(res.data) ? res.data : []);
        }
      } catch (error) {
        if (active) {
          setModuleRows([]);
        }
      } finally {
        if (active) {
          setLoadingModules(false);
        }
      }
    };

    loadModules();

    return () => {
      active = false;
    };
  }, [item]);

  if (!item) return null;

  const semesters = groupCourseSyllabus(moduleRows);

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15, 23, 42, 0.72)",
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "20px",
      }}
      onClick={onClose}
    >
      <div
        className="bg-white rounded-4 shadow-lg"
        style={{ width: "min(1100px, 100%)", maxHeight: "calc(100vh - 40px)", overflowY: "auto" }}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="p-4 border-bottom d-flex justify-content-between align-items-start gap-3">
          <div>
            <h3 className="mb-1">{item.title}</h3>
            <div className="text-muted small">
              {item.model_name || "-"} | {item.category_name || "-"} | {Number(item.semester_count || 1)} semester(s)
            </div>
          </div>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onClose}>
            Close
          </button>
        </div>

        <div className="p-4 d-grid gap-4">
          <div className="row g-3">
            <div className="col-md-3"><div className="border rounded-3 p-3 h-100"><div className="text-muted small">Wallet Deduction</div><div className="fw-semibold">Rs {Number(item.wallet_deduction || item.fee_amount || 0).toFixed(2)}</div></div></div>
            <div className="col-md-3"><div className="border rounded-3 p-3 h-100"><div className="text-muted small">Tools Point</div><div className="fw-semibold">{Number(item.tools_points || 0).toFixed(2)}</div></div></div>
            <div className="col-md-3"><div className="border rounded-3 p-3 h-100"><div className="text-muted small">Gift Card Point</div><div className="fw-semibold">{Number(item.gift_card_points || 0).toFixed(2)}</div></div></div>
            <div className="col-md-3"><div className="border rounded-3 p-3 h-100"><div className="text-muted small">Skill Point</div><div className="fw-semibold">{Number(item.skill_points || 0).toFixed(2)}</div></div></div>
          </div>

          <div>
            <h5 className="mb-2">Description</h5>
            <div className="border rounded-3 p-3 bg-light" style={{ whiteSpace: "pre-wrap" }}>
              {item.description || "No description added yet."}
            </div>
          </div>

          <div>
            <h5 className="mb-3">Semester Wise Syllabus</h5>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: "14px" }}>
              {loadingModules ? (
                <div className="border rounded-4 p-3 bg-light h-100">Loading semester details...</div>
              ) : null}
            {semesters.map((semester) => (
              <div key={`preview-${item.id}-${semester.semester_no}`} className="border rounded-4 p-3 bg-light h-100">
                <div className="fw-bold mb-3">{semester.semester_title}</div>
                {semester.items.map((module, index) => (
                  <div key={`${item.id}-${semester.semester_no}-${module.id || index}`} className="border rounded-3 bg-white p-2 mb-2">
                    <div className="d-flex justify-content-between align-items-start gap-3">
                      <div className="fw-semibold">{module.module_title}</div>
                      <div className="small fw-semibold text-primary">Total: {formatMarks(totalModuleMarks(module))}</div>
                    </div>
                    <div className="small text-primary mb-1">
                      Theory {formatMarks(module.max_marks)} | Practical {formatMarks(module.practical_max_marks)} | Project {formatMarks(module.project_max_marks)} | Viva {formatMarks(module.viva_max_marks)}
                    </div>
                    <div className="small text-muted mb-1" style={{ whiteSpace: "pre-wrap" }}>{module.description || "-"}</div>
                  </div>
                ))}
                <div className="fw-semibold text-primary mt-2">Semester Total: {formatMarks(semesterTotalMarks(semester))}</div>
              </div>
            ))}
              {!loadingModules && !semesters.length ? <div className="border rounded-4 p-3 bg-light h-100">No syllabus configured for this course.</div> : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function usePortalData(endpoint, fallback) {
  const { franchise } = useOutletContext();
  const [data, setData] = useState(fallback);
  const [loading, setLoading] = useState(true);
  const fallbackRef = useRef(fallback);
  const requestPath = franchise?.id && endpoint ? endpoint(franchise.id) : "";

  useEffect(() => {
    if (!franchise?.id || !requestPath) {
      setLoading(false);
      return;
    }

    let active = true;
    const cacheKey = `portal-cache:${requestPath}`;
    const cachedValue = sessionStorage.getItem(cacheKey);

    if (cachedValue) {
      try {
        const parsed = JSON.parse(cachedValue);
        if (Date.now() - Number(parsed.timestamp || 0) < 5 * 60 * 1000) {
          setData(parsed.data);
          setLoading(false);
        }
      } catch (error) {
        sessionStorage.removeItem(cacheKey);
      }
    }

    const load = async () => {
      try {
        const res = await axios.get(`${API}${requestPath}`);
        if (active) {
          setData(res.data);
          sessionStorage.setItem(cacheKey, JSON.stringify({ timestamp: Date.now(), data: res.data }));
        }
      } catch (error) {
        if (active) {
          setData(fallbackRef.current);
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      active = false;
    };
  }, [franchise?.id, requestPath]);

  return { data, setData, loading, franchise };
}

function ModuleHeader({ title, description }) {
  return (
    <div
      style={{
        marginBottom: "20px",
        background: "linear-gradient(135deg, rgba(219,234,254,0.62) 0%, rgba(255,247,237,0.92) 100%)",
        border: "1px solid rgba(148, 163, 184, 0.14)",
        borderRadius: "22px",
        padding: "18px 20px",
      }}
    >
      <h2 style={{ margin: "0 0 6px", color: "#4e1b24" }}>{title}</h2>
      <p style={{ color: "#6b3140", lineHeight: 1.7, marginBottom: 0 }}>{description}</p>
    </div>
  );
}

export function Overview() {
  const { summary } = useOutletContext();
  const walletItems = summary.wallets || [];

  return (
    <div style={{ display: "grid", gap: "20px" }}>
      <ModuleHeader title="Dashboard Overview" description="Quick summary of multi-wallet balance, applications, exams, messages, and certificates for this franchise account." />
      <div style={cardGrid}>
        <div style={statCard}><p style={{ margin: 0, color: "#64748b", fontWeight: 700 }}>Wallet Balance</p><h3 style={{ margin: "10px 0 0" }}>Rs {Number(summary.wallet_balance || 0).toFixed(2)}</h3></div>
        {walletItems.map((wallet) => <div key={wallet.key} style={statCard}><p style={{ margin: 0, color: "#64748b", fontWeight: 700 }}>{walletLabel(wallet.label, wallet.key)}</p><h3 style={{ margin: "10px 0 0" }}>{wallet.key === "gift_card_wallet" || wallet.key === "reserve_wallet" || wallet.key === "tools_wallet" ? Number(wallet.balance || 0).toFixed(2) : `Rs ${Number(wallet.balance || 0).toFixed(2)}`}</h3></div>)}
        <div style={statCard}><p style={{ margin: 0, color: "#64748b", fontWeight: 700 }}>Our Students</p><h3 style={{ margin: "10px 0 0" }}>{summary.application_count || 0}</h3></div>
        <div style={statCard}><p style={{ margin: 0, color: "#64748b", fontWeight: 700 }}>Courses</p><h3 style={{ margin: "10px 0 0" }}>{summary.course_count || 0}</h3></div>
        <div style={statCard}><p style={{ margin: 0, color: "#64748b", fontWeight: 700 }}>Exams</p><h3 style={{ margin: "10px 0 0" }}>{summary.exam_count || 0}</h3></div>
      </div>
    </div>
  );
}

export function CourseView() {
  const { data: courses = [], loading } = usePortalData((id) => `/franchise-portal/${id}/courses`, []);
  const [viewportWidth, setViewportWidth] = useState(() => (typeof window !== "undefined" ? window.innerWidth : 1280));
  const [filters, setFilters] = useState({ search: "", duration: "" });
  const [previewCourse, setPreviewCourse] = useState(null);
  const [trendingItemsPerPage, setTrendingItemsPerPage] = useState("5");
  const [allItemsPerPage, setAllItemsPerPage] = useState("10");
  const [trendingPage, setTrendingPage] = useState(1);
  const [allPage, setAllPage] = useState(1);

  useEffect(() => {
    const handleResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const isMobile = viewportWidth < 768;
  const isTablet = viewportWidth >= 768 && viewportWidth < 1200;
  const rowGridTemplate = isMobile
    ? "1fr"
    : isTablet
      ? "1fr 1.2fr 1fr 1fr"
      : "auto 1.25fr 1fr 1.2fr 0.9fr 0.9fr 1fr auto";
  const durationOptions = useMemo(() => {
    const seen = new Map();
    courses.forEach((course) => {
      const value = String(course.duration || "").trim();
      if (value) {
        seen.set(value.toLowerCase(), value);
      }
    });
    return [...seen.values()];
  }, [courses]);
  const filteredCourses = useMemo(() => {
    const searchValue = filters.search.trim().toLowerCase();
    const durationValue = filters.duration.trim().toLowerCase();

    return courses.filter((course) => {
      const title = String(course.title || "").toLowerCase();
      const duration = String(course.duration || "").toLowerCase();

      if (searchValue && !title.includes(searchValue)) {
        return false;
      }

      if (durationValue && duration !== durationValue) {
        return false;
      }

      return true;
    });
  }, [courses, filters]);
  const trendingCourses = useMemo(() => filteredCourses.filter((course) => Boolean(course.is_trending)), [filteredCourses]);
  const trendingEffectiveItemsPerPage = trendingItemsPerPage === "All" ? trendingCourses.length || 1 : Number(trendingItemsPerPage || 5);
  const allEffectiveItemsPerPage = allItemsPerPage === "All" ? filteredCourses.length || 1 : Number(allItemsPerPage || 10);
  const trendingTotalPages = Math.max(1, Math.ceil(trendingCourses.length / trendingEffectiveItemsPerPage));
  const allTotalPages = Math.max(1, Math.ceil(filteredCourses.length / allEffectiveItemsPerPage));
  const paginatedTrendingCourses = useMemo(() => {
    if (trendingItemsPerPage === "All") {
      return trendingCourses;
    }

    const start = (trendingPage - 1) * trendingEffectiveItemsPerPage;
    return trendingCourses.slice(start, start + trendingEffectiveItemsPerPage);
  }, [trendingCourses, trendingPage, trendingItemsPerPage, trendingEffectiveItemsPerPage]);
  const paginatedAllCourses = useMemo(() => {
    if (allItemsPerPage === "All") {
      return filteredCourses;
    }

    const start = (allPage - 1) * allEffectiveItemsPerPage;
    return filteredCourses.slice(start, start + allEffectiveItemsPerPage);
  }, [filteredCourses, allPage, allItemsPerPage, allEffectiveItemsPerPage]);

  useEffect(() => {
    setTrendingPage(1);
  }, [trendingItemsPerPage, trendingCourses.length, filters.search, filters.duration]);

  useEffect(() => {
    setAllPage(1);
  }, [allItemsPerPage, filteredCourses.length, filters.search, filters.duration]);

  useEffect(() => {
    if (trendingPage > trendingTotalPages) {
      setTrendingPage(trendingTotalPages);
    }
  }, [trendingPage, trendingTotalPages]);

  useEffect(() => {
    if (allPage > allTotalPages) {
      setAllPage(allTotalPages);
    }
  }, [allPage, allTotalPages]);

  return (
    <div>
      <ModuleHeader title="View Course" description="Assigned courses are shown here with thumbnail, fees, duration, category, and preview action for this franchise account." />
      {loading ? (
        <p>Loading courses...</p>
      ) : (
        <div style={{ display: "grid", gap: "16px" }}>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1.2fr 0.8fr", gap: "12px" }}>
            <input
              className="form-control"
              placeholder="Search by course name"
              value={filters.search}
              onChange={(event) => setFilters((prev) => ({ ...prev, search: event.target.value }))}
            />
            <select
              className="form-select"
              value={filters.duration}
              onChange={(event) => setFilters((prev) => ({ ...prev, duration: event.target.value }))}
            >
              <option value="">All Durations</option>
              {durationOptions.map((duration) => (
                <option key={duration} value={duration}>{duration}</option>
              ))}
            </select>
          </div>

          <div style={{ display: "grid", gap: "10px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: "10px", alignItems: "center", flexWrap: "wrap" }}>
              <h3 style={{ margin: 0, color: "#0f172a" }}>Trending Course</h3>
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <span style={{ color: "#475569", fontWeight: 600, fontSize: "13px" }}>Show</span>
                <select
                  className="form-select form-select-sm"
                  style={{ width: "110px" }}
                  value={trendingItemsPerPage}
                  onChange={(event) => setTrendingItemsPerPage(event.target.value)}
                >
                  <option value="5">5</option>
                  <option value="10">10</option>
                  <option value="25">25</option>
                  <option value="50">50</option>
                  <option value="All">All</option>
                </select>
              </div>
            </div>
            <div style={rowListWrapStyle}>
              {paginatedTrendingCourses.map((course, index) => (
                <div key={`trending-${course.id}`} style={rowCardStyle}>
                  <div style={{ display: "grid", gridTemplateColumns: rowGridTemplate, gap: "10px", alignItems: "stretch", width: "100%" }}>
                    <div style={rowCellStyle}>
                      <div style={rowLabelStyle}>Sl No</div>
                      <div style={rowContentStyle}>
                        <div className="fw-semibold" style={rowValueStyle}>{(trendingPage - 1) * trendingEffectiveItemsPerPage + index + 1}</div>
                      </div>
                    </div>

                    <div style={rowCellStyle}>
                      <div style={rowLabelStyle}>Thumbnail</div>
                      <div style={rowContentStyle}>
                        <div style={{ position: "relative", width: "100%", maxWidth: isMobile ? "100%" : "160px" }}>
                          <img
                            src={course.thumbnail ? `${THUMBNAIL_ROOT}/${course.thumbnail}` : "https://placehold.co/220x140/e2e8f0/475569?text=Course"}
                            alt={course.title || "Course thumbnail"}
                            style={{ width: "100%", height: isMobile ? "180px" : "120px", objectFit: "cover", borderRadius: "12px", border: "1px solid #e2e8f0", display: "block" }}
                          />
                          <span
                            style={{
                              position: "absolute",
                              top: "10px",
                              left: "10px",
                              background: "linear-gradient(135deg, #7c2d3a 0%, #c2410c 100%)",
                              color: "#fff",
                              borderRadius: "999px",
                              padding: "5px 10px",
                              fontSize: "10px",
                              fontWeight: 700,
                              boxShadow: "0 8px 18px rgba(124, 45, 58, 0.24)",
                            }}
                          >
                            Trending
                          </span>
                        </div>
                      </div>
                    </div>

                    <div style={rowCellStyle}>
                      <div style={rowLabelStyle}>Title</div>
                      <div style={rowContentStyle}>
                        <div className="fw-bold" style={{ ...rowValueStyle, fontSize: "14px", textAlign: isMobile ? "center" : "left" }}>{course.title || "-"}</div>
                      </div>
                    </div>

                    <div style={rowCellStyle}>
                      <div style={rowLabelStyle}>Category</div>
                      <div style={rowContentStyle}>
                        <div style={rowMutedStyle}>{course.category_name || course.category || "-"}</div>
                      </div>
                    </div>

                    <div style={rowCellStyle}>
                      <div style={rowLabelStyle}>Duration</div>
                      <div style={rowContentStyle}>
                        <div style={rowMutedStyle}>{course.duration || "-"}</div>
                      </div>
                    </div>

                    <div style={rowCellStyle}>
                      <div style={rowLabelStyle}>Fee</div>
                      <div style={rowContentStyle}>
                        <div style={rowMutedStyle}>Rs {Number(course.fee_amount || 0).toFixed(2)}</div>
                      </div>
                    </div>

                    <div style={rowCellStyle}>
                      <div style={rowLabelStyle}>Syllabus</div>
                      <div style={rowContentStyle}>
                        <div style={rowMutedStyle}>
                          {Number(course.semester_count || 1)} Semester{Number(course.semester_count || 1) > 1 ? "s" : ""}
                        </div>
                      </div>
                    </div>

                    <div style={{ ...rowCellStyle, paddingRight: 0, borderRight: "none" }}>
                      <div style={rowLabelStyle}>Action</div>
                      <div style={rowContentStyle}>
                        <button
                          type="button"
                          className="btn btn-outline-primary btn-sm"
                          onClick={() => setPreviewCourse(course)}
                        >
                          View
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {!trendingCourses.length ? (
                <div className="text-center text-muted py-4" style={rowCardStyle}>No trending course available for this franchise.</div>
              ) : null}
            </div>
            {trendingItemsPerPage !== "All" && trendingTotalPages > 1 ? (
              <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", flexWrap: "wrap" }}>
                <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setTrendingPage((prev) => Math.max(1, prev - 1))} disabled={trendingPage === 1}>
                  Prev
                </button>
                <div style={{ display: "flex", alignItems: "center", padding: "0 10px", color: "#475569", fontWeight: 600 }}>
                  {trendingPage} / {trendingTotalPages}
                </div>
                <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setTrendingPage((prev) => Math.min(trendingTotalPages, prev + 1))} disabled={trendingPage === trendingTotalPages}>
                  Next
                </button>
              </div>
            ) : null}
          </div>

          <div style={{ display: "grid", gap: "10px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: "10px", alignItems: "center", flexWrap: "wrap" }}>
              <h3 style={{ margin: 0, color: "#0f172a" }}>All Course</h3>
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <span style={{ color: "#475569", fontWeight: 600, fontSize: "13px" }}>Show</span>
                <select
                  className="form-select form-select-sm"
                  style={{ width: "110px" }}
                  value={allItemsPerPage}
                  onChange={(event) => setAllItemsPerPage(event.target.value)}
                >
                  <option value="5">5</option>
                  <option value="10">10</option>
                  <option value="25">25</option>
                  <option value="50">50</option>
                  <option value="All">All</option>
                </select>
              </div>
            </div>
            <div style={rowListWrapStyle}>
          {paginatedAllCourses.map((course, index) => (
            <div key={course.id} style={rowCardStyle}>
              <div style={{ display: "grid", gridTemplateColumns: rowGridTemplate, gap: "10px", alignItems: "stretch", width: "100%" }}>
                <div style={rowCellStyle}>
                  <div style={rowLabelStyle}>Sl No</div>
                  <div style={rowContentStyle}>
                    <div className="fw-semibold" style={rowValueStyle}>{(allPage - 1) * allEffectiveItemsPerPage + index + 1}</div>
                  </div>
                </div>

                <div style={rowCellStyle}>
                  <div style={rowLabelStyle}>Thumbnail</div>
                  <div style={rowContentStyle}>
                    <div style={{ position: "relative", width: "100%", maxWidth: isMobile ? "100%" : "160px" }}>
                      <img
                        src={course.thumbnail ? `${THUMBNAIL_ROOT}/${course.thumbnail}` : "https://placehold.co/220x140/e2e8f0/475569?text=Course"}
                        alt={course.title || "Course thumbnail"}
                        style={{ width: "100%", height: isMobile ? "180px" : "120px", objectFit: "cover", borderRadius: "12px", border: "1px solid #e2e8f0", display: "block" }}
                      />
                    </div>
                  </div>
                </div>

                <div style={rowCellStyle}>
                  <div style={rowLabelStyle}>Title</div>
                  <div style={rowContentStyle}>
                    <div className="fw-bold" style={{ ...rowValueStyle, fontSize: "14px", textAlign: isMobile ? "center" : "left" }}>{course.title || "-"}</div>
                  </div>
                </div>

                <div style={rowCellStyle}>
                  <div style={rowLabelStyle}>Category</div>
                  <div style={rowContentStyle}>
                    <div style={rowMutedStyle}>{course.category_name || course.category || "-"}</div>
                  </div>
                </div>

                <div style={rowCellStyle}>
                  <div style={rowLabelStyle}>Duration</div>
                  <div style={rowContentStyle}>
                    <div style={rowMutedStyle}>{course.duration || "-"}</div>
                  </div>
                </div>

                <div style={rowCellStyle}>
                  <div style={rowLabelStyle}>Fee</div>
                  <div style={rowContentStyle}>
                    <div style={rowMutedStyle}>Rs {Number(course.fee_amount || 0).toFixed(2)}</div>
                  </div>
                </div>

                <div style={rowCellStyle}>
                  <div style={rowLabelStyle}>Syllabus</div>
                  <div style={rowContentStyle}>
                    <div style={rowMutedStyle}>
                      {Number(course.semester_count || 1)} Semester{Number(course.semester_count || 1) > 1 ? "s" : ""}
                    </div>
                  </div>
                </div>

                <div style={{ ...rowCellStyle, paddingRight: 0, borderRight: "none" }}>
                  <div style={rowLabelStyle}>Action</div>
                  <div style={rowContentStyle}>
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm"
                      onClick={() => setPreviewCourse(course)}
                    >
                      View
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}

          {!filteredCourses.length ? (
            <div className="text-center text-muted py-4" style={rowCardStyle}>No course available for this franchise.</div>
          ) : null}
            </div>
            {allItemsPerPage !== "All" && allTotalPages > 1 ? (
              <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", flexWrap: "wrap" }}>
                <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setAllPage((prev) => Math.max(1, prev - 1))} disabled={allPage === 1}>
                  Prev
                </button>
                <div style={{ display: "flex", alignItems: "center", padding: "0 10px", color: "#475569", fontWeight: 600 }}>
                  {allPage} / {allTotalPages}
                </div>
                <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setAllPage((prev) => Math.min(allTotalPages, prev + 1))} disabled={allPage === allTotalPages}>
                  Next
                </button>
              </div>
            ) : null}
          </div>
        </div>
      )}
      <CoursePreviewModal item={previewCourse} onClose={() => setPreviewCourse(null)} />
    </div>
  );
}

export function CourseFees() {
  const { data: courses } = usePortalData((id) => `/franchise-portal/${id}/courses`, []);
  const [itemsPerPage, setItemsPerPage] = useState("10");
  const [page, setPage] = useState(1);

  const exportRows = useMemo(
    () =>
      courses.map((course) => ({
        course: course.title || "-",
        fee: `Rs ${Number(course.fee_amount || 0).toFixed(2)}`,
        duration: course.duration || "-",
        syllabus: `${Number(course.semester_count || 1)} Semester${Number(course.semester_count || 1) > 1 ? "s" : ""}`,
      })),
    [courses]
  );
  const totalRows = courses.length;
  const effectiveItemsPerPage = itemsPerPage === "All" ? totalRows || 1 : Number(itemsPerPage || 10);
  const totalPages = Math.max(1, Math.ceil(totalRows / effectiveItemsPerPage));
  const paginatedCourses = useMemo(() => {
    if (itemsPerPage === "All") {
      return courses;
    }

    const start = (page - 1) * effectiveItemsPerPage;
    return courses.slice(start, start + effectiveItemsPerPage);
  }, [courses, page, itemsPerPage, effectiveItemsPerPage]);

  useEffect(() => {
    setPage(1);
  }, [itemsPerPage, totalRows]);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const downloadTextFile = (content, fileName, type) => {
    const blob = new Blob([content], { type });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleCsvDownload = () => {
    const header = ["Course", "Fee", "Course Duration", "Syllabus Details"];
    const lines = exportRows.map((row) => [row.course, row.fee, row.duration, row.syllabus].map((value) => `"${String(value).replace(/"/g, '""')}"`).join(","));
    downloadTextFile([header.join(","), ...lines].join("\n"), "course-fees.csv", "text/csv;charset=utf-8;");
  };

  const handleExcelDownload = () => {
    const rows = exportRows
      .map(
        (row) =>
          `<tr><td>${escapeHtml(row.course)}</td><td>${escapeHtml(row.fee)}</td><td>${escapeHtml(row.duration)}</td><td>${escapeHtml(row.syllabus)}</td></tr>`
      )
      .join("");

    const html = `
      <table>
        <thead>
          <tr>
            <th>Course</th>
            <th>Fee</th>
            <th>Course Duration</th>
            <th>Syllabus Details</th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>
    `;

    downloadTextFile(html, "course-fees.xls", "application/vnd.ms-excel;charset=utf-8;");
  };

  const handlePdfDownload = () => {
    const rows = exportRows
      .map(
        (row) =>
          `<tr><td>${escapeHtml(row.course)}</td><td>${escapeHtml(row.fee)}</td><td>${escapeHtml(row.duration)}</td><td>${escapeHtml(row.syllabus)}</td></tr>`
      )
      .join("");

    printHtmlDocument(
      "Course Fees",
      `
        <div class="sheet">
          <div class="hero">
            <h2 style="margin:0 0 6px;">View Course Fees</h2>
            <p style="margin:0;opacity:.86;">Course fee slab with duration and semester details.</p>
          </div>
          <div class="body">
            <table>
              <thead>
                <tr>
                  <th>Course</th>
                  <th>Fee</th>
                  <th>Course Duration</th>
                  <th>Syllabus Details</th>
                </tr>
              </thead>
              <tbody>${rows}</tbody>
            </table>
          </div>
        </div>
      `
    );
  };

  return (
    <div>
      <ModuleHeader title="View Course Fees" description="Course fee slab visible for the current franchise account." />
      <div style={{ ...statCard, marginBottom: "16px", display: "flex", justifyContent: "space-between", gap: "10px", flexWrap: "wrap", alignItems: "center" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
          <span style={{ color: "#475569", fontWeight: 600, fontSize: "13px" }}>Show</span>
          <select
            className="form-select form-select-sm"
            style={{ width: "110px" }}
            value={itemsPerPage}
            onChange={(event) => setItemsPerPage(event.target.value)}
          >
            <option value="5">5</option>
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="All">All</option>
          </select>
        </div>
        <button type="button" className="btn btn-outline-success btn-sm" onClick={handleExcelDownload}>Excel</button>
        <button type="button" className="btn btn-outline-primary btn-sm" onClick={handleCsvDownload}>CSV</button>
        <button type="button" className="btn btn-outline-danger btn-sm" onClick={handlePdfDownload}>PDF</button>
      </div>
      <div style={tableWrap}>
        <table style={tableStyle}><thead><tr><th style={{ ...thStyle, ...dividerCellStyle, ...centerCellStyle, ...autoWidthCellStyle }}>Sl No</th><th style={{ ...thStyle, ...dividerCellStyle }}>Course</th><th style={{ ...thStyle, ...dividerCellStyle }}>Course Fee</th><th style={{ ...thStyle, ...dividerCellStyle }}>Course Duration</th><th style={thStyle}>Syllabus Details</th></tr></thead><tbody>
          {paginatedCourses.map((course, index) => <tr key={course.id}><td style={{ ...tdStyle, ...dividerCellStyle, ...centerCellStyle, ...autoWidthCellStyle }}>{(page - 1) * effectiveItemsPerPage + index + 1}</td><td style={{ ...tdStyle, ...dividerCellStyle }}>{course.title}</td><td style={{ ...tdStyle, ...dividerCellStyle, textAlign: "center" }}>Rs {Number(course.fee_amount || 0).toFixed(2)}</td><td style={{ ...tdStyle, ...dividerCellStyle, textAlign: "center" }}>{course.duration || "-"}</td><td style={{ ...tdStyle, textAlign: "center" }}>{Number(course.semester_count || 1)} Semester{Number(course.semester_count || 1) > 1 ? "s" : ""}</td></tr>)}
        </tbody></table>
      </div>
      {itemsPerPage !== "All" && totalPages > 1 ? (
        <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "16px", flexWrap: "wrap" }}>
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setPage((prev) => Math.max(1, prev - 1))} disabled={page === 1}>
            Prev
          </button>
          <div style={{ display: "flex", alignItems: "center", padding: "0 10px", color: "#475569", fontWeight: 600 }}>
            {page} / {totalPages}
          </div>
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))} disabled={page === totalPages}>
            Next
          </button>
        </div>
      ) : null}
    </div>
  );
}

export function NewApplication() {
  const { franchise, activeSession } = useOutletContext();
  const { data: courses } = usePortalData((id) => `/franchise-portal/${id}/courses`, []);
  const calculateCompletionDate = (admissionDate, courseId) => {
    if (!admissionDate || !courseId) return "";
    const course = courses.find((item) => String(item.id) === String(courseId));
    const duration = String(course?.duration || "").toLowerCase();
    const matches = [...duration.matchAll(/(\d+(?:\.\d+)?)\s*(year|years|yr|yrs|month|months|mon|mons|week|weeks|day|days)/g)];
    if (!matches.length) return "";

    const date = new Date(`${admissionDate}T00:00:00`);
    if (Number.isNaN(date.getTime())) return "";

    matches.forEach((match) => {
      const amount = Number(match[1]);
      const unit = match[2];
      if (unit.startsWith("year") || unit.startsWith("yr")) {
        date.setMonth(date.getMonth() + Math.round(amount * 12));
      } else if (unit.startsWith("month") || unit.startsWith("mon")) {
        date.setMonth(date.getMonth() + Math.round(amount));
      } else if (unit.startsWith("week")) {
        date.setDate(date.getDate() + Math.round(amount * 7));
      } else if (unit.startsWith("day")) {
        date.setDate(date.getDate() + Math.round(amount));
      }
    });

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };
  const [form, setForm] = useState({
    student_name: "",
    phone: "",
    email: "",
    dob: "",
    father_name: "",
    admission_date: "",
    course_completion_date: "",
    guardian_name: "",
    address: "",
    course_id: "",
  });
  const [files, setFiles] = useState({
    photo: null,
    signature: null,
    id_proof: null,
    academic_document: null,
  });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => {
      const next = { ...prev, [name]: value };
      if (name === "course_id" || name === "admission_date") {
        next.course_completion_date = calculateCompletionDate(next.admission_date, next.course_id);
      }
      return next;
    });
  };

  const handleFileChange = (event) => {
    const { name, files: selectedFiles } = event.target;
    setFiles((prev) => ({ ...prev, [name]: selectedFiles?.[0] || null }));
  };

  const handleSubmit = async () => {
    if (!franchise?.id) return;

    const requiredFields = [
      ["student_name", "Student name"],
      ["phone", "Phone"],
      ["email", "Email"],
      ["dob", "Date of birth"],
      ["father_name", "Father name"],
      ["course_id", "Course"],
      ["admission_date", "Admission date"],
      ["course_completion_date", "Course completion"],
      ["guardian_name", "Guardian name"],
      ["address", "Address"],
    ];
    const missingField = requiredFields.find(([key]) => !String(form[key] || "").trim());

    if (missingField) {
      setMessage(`${missingField[1]} is required.`);
      return;
    }

    const missingFile = Object.entries(files).find(([, value]) => !value);
    if (missingFile) {
      setMessage(`${missingFile[0].replaceAll("_", " ")} is required.`);
      return;
    }

    try {
      setSaving(true);
      setMessage("");
      const payload = new FormData();
      Object.entries({
        ...form,
        course_id: form.course_id || "",
        session_year: activeSession === "All Sessions" ? "" : activeSession,
      }).forEach(([key, value]) => payload.append(key, value || ""));
      Object.entries(files).forEach(([key, value]) => {
        if (value) payload.append(key, value);
      });

      const res = await axios.post(`${API}/franchise-portal/${franchise.id}/applications`, payload, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setMessage(res.data?.message || "Student application saved successfully");
      setForm({
        student_name: "",
        phone: "",
        email: "",
        dob: "",
        father_name: "",
        admission_date: "",
        course_completion_date: "",
        guardian_name: "",
        address: "",
        course_id: "",
      });
      setFiles({
        photo: null,
        signature: null,
        id_proof: null,
        academic_document: null,
      });
    } catch (error) {
      setMessage(error.response?.data?.message || "Student application save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <ModuleHeader title="New Application" description="Add a new student from the franchise panel. Saved data will instantly appear in franchise View Student and admin View Application." />
      <div style={{ ...statCard, marginBottom: "16px", background: "linear-gradient(135deg, #eff6ff 0%, #fff7ed 100%)" }}>
        <strong style={{ color: "#0f172a" }}>Active Session:</strong> {activeSession}
        <div style={{ marginTop: "8px", color: "#64748b", lineHeight: 1.6 }}>
          This session is controlled from master admin and only this active session will be saved with every new student admission.
        </div>
      </div>
      <div style={cardGrid}>
        <div style={statCard}>
          <label style={{ display: "block", marginBottom: "8px", color: "#475569", fontWeight: 600 }}>Student Name</label>
          <input className="form-control" name="student_name" value={form.student_name} onChange={handleChange} placeholder="Student Name" required />
        </div>
        <div style={statCard}>
          <label style={{ display: "block", marginBottom: "8px", color: "#475569", fontWeight: 600 }}>Phone</label>
          <input className="form-control" name="phone" value={form.phone} onChange={handleChange} placeholder="Phone Number" required />
        </div>
        <div style={statCard}>
          <label style={{ display: "block", marginBottom: "8px", color: "#475569", fontWeight: 600 }}>Email</label>
          <input className="form-control" type="email" name="email" value={form.email} onChange={handleChange} placeholder="Email Address" required />
        </div>
        <div style={statCard}>
          <label style={{ display: "block", marginBottom: "8px", color: "#475569", fontWeight: 600 }}>Date of Birth</label>
          <input className="form-control" type="date" name="dob" value={form.dob} onChange={handleChange} required />
        </div>
        <div style={statCard}>
          <label style={{ display: "block", marginBottom: "8px", color: "#475569", fontWeight: 600 }}>Father Name</label>
          <input className="form-control" name="father_name" value={form.father_name} onChange={handleChange} placeholder="Father Name" required />
        </div>
        <div style={statCard}>
          <label style={{ display: "block", marginBottom: "8px", color: "#475569", fontWeight: 600 }}>Course</label>
          <select className="form-select" name="course_id" value={form.course_id} onChange={handleChange} required>
            <option value="">Select Course</option>
            {courses.map((course) => (
              <option key={course.id} value={course.id}>
                {course.title}
              </option>
            ))}
          </select>
        </div>
        <div style={statCard}>
          <label style={{ display: "block", marginBottom: "8px", color: "#475569", fontWeight: 600 }}>Admission Date</label>
          <input className="form-control" type="date" name="admission_date" value={form.admission_date} onChange={handleChange} required />
        </div>
        <div style={statCard}>
          <label style={{ display: "block", marginBottom: "8px", color: "#475569", fontWeight: 600 }}>Course Completion</label>
          <input className="form-control" type="date" name="course_completion_date" value={form.course_completion_date} readOnly required />
        </div>
        <div style={statCard}>
          <label style={{ display: "block", marginBottom: "8px", color: "#475569", fontWeight: 600 }}>Guardian Name</label>
          <input className="form-control" name="guardian_name" value={form.guardian_name} onChange={handleChange} placeholder="Guardian Name" required />
        </div>
        <div style={{ ...statCard, gridColumn: "1 / -1" }}>
          <label style={{ display: "block", marginBottom: "8px", color: "#475569", fontWeight: 600 }}>Address</label>
          <textarea className="form-control" rows="4" name="address" value={form.address} onChange={handleChange} placeholder="Full address" required></textarea>
        </div>
        {[
          ["photo", "Photo"],
          ["signature", "Signature"],
          ["id_proof", "ID Proof"],
          ["academic_document", "Academic Document"],
        ].map(([key, label]) => (
          <div key={key} style={statCard}>
            <label style={{ display: "block", marginBottom: "8px", color: "#475569", fontWeight: 600 }}>{label}</label>
            <input className="form-control" type="file" name={key} onChange={handleFileChange} required />
          </div>
        ))}
      </div>
      <div style={{ ...statCard, marginTop: "16px", display: "grid", gap: "12px" }}>
        <div>
          Applications created from this panel will belong to franchise ID <strong>{franchise?.id}</strong>.
        </div>
        {message ? <div style={{ color: message.toLowerCase().includes("failed") || message.toLowerCase().includes("required") ? "#b91c1c" : "#166534", fontWeight: 700 }}>{message}</div> : null}
        <div>
          <button type="button" className="btn btn-primary" onClick={handleSubmit} disabled={saving}>
            {saving ? "Saving..." : "Save Student Application"}
          </button>
        </div>
      </div>
    </div>
  );
}

export function ViewApplication() {
  const { franchise, selectedSession } = useOutletContext();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [marksData, setMarksData] = useState(null);
  const [marksLoading, setMarksLoading] = useState(false);
  const [marksSaving, setMarksSaving] = useState(false);

  const loadApplications = useCallback(async () => {
    if (!franchise?.id) return;
    try {
      setLoading(true);
      const res = await axios.get(`${API}/franchise-portal/${franchise.id}/applications`, {
        params: {
          session_year: selectedSession,
          _ts: Date.now(),
        },
      });
      setApplications(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      setApplications([]);
    } finally {
      setLoading(false);
    }
  }, [franchise?.id, selectedSession]);

  useEffect(() => {
    loadApplications();
  }, [loadApplications]);

  const openMarksEntry = async (applicationId) => {
    try {
      setMarksLoading(true);
      const res = await axios.get(`${API}/student-applications/${applicationId}/marks`);
      setMarksData(res.data);
    } catch (error) {
      window.alert(error.response?.data?.message || "Marks data load failed");
    } finally {
      setMarksLoading(false);
    }
  };

  const updateMarksRow = (moduleId, key, value) => {
    setMarksData((prev) => ({
      ...prev,
      rows: prev.rows.map((row) => (row.module_id === moduleId ? { ...row, [key]: value } : row)),
    }));
  };

  const submitMarks = async () => {
    if (!marksData?.application?.id) return;

    try {
      setMarksSaving(true);
      await axios.post(`${API}/student-applications/${marksData.application.id}/marks`, {
        rows: marksData.rows,
      });
      setMarksData(null);
      window.alert("Marks submitted for master admin approval");
    } catch (error) {
      window.alert(error.response?.data?.message || "Marks save failed");
    } finally {
      setMarksSaving(false);
    }
  };

  return (
    <div>
      <ModuleHeader title="View Student" description={`Session ${selectedSession || "-"} অনুযায়ী franchise student applications show হচ্ছে এবং same data admin panel-এও visible.`} />
      <div style={tableWrap}>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Sl No</th>
              <th style={thStyle}>Registration No</th>
              <th style={thStyle}>Name</th>
              <th style={thStyle}>Study Centre Name</th>
              <th style={thStyle}>State</th>
              <th style={thStyle}>District</th>
              <th style={thStyle}>Status</th>
              <th style={thStyle}>Action</th>
            </tr>
          </thead>
          <tbody>
            {!loading && applications.map((item, index) => (
              <tr key={item.id}>
                <td style={tdStyle}>{index + 1}</td>
                <td style={tdStyle}>{item.registration_no || "-"}</td>
                <td style={tdStyle}>
                  <strong>{item.student_name}</strong>
                  <div style={{ color: "#64748b", fontSize: "12px", marginTop: "4px" }}>{item.application_no || `APP-${item.id}`}</div>
                  <div style={{ color: "#7c2d3a", fontSize: "12px", marginTop: "4px", fontWeight: 700 }}>{item.session_year || "-"}</div>
                </td>
                <td style={tdStyle}>{item.study_centre_name || "-"}</td>
                <td style={tdStyle}>{item.state_name || "-"}</td>
                <td style={tdStyle}>{item.district_name || "-"}</td>
                <td style={tdStyle}><StatusChip status={item.status} /></td>
                <td style={tdStyle}>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                    <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => setSelectedApplication(item)}>
                      View
                    </button>
                    <button type="button" className="btn btn-sm btn-outline-success" onClick={() => openMarksEntry(item.id)}>
                      Marks Entry
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {!loading && applications.length === 0 ? (
              <tr>
                <td style={tdStyle} colSpan={8}>No student application found for session {selectedSession || "-"}.</td>
              </tr>
            ) : null}
            {loading ? (
              <tr>
                <td style={tdStyle} colSpan={8}>Loading student applications...</td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      <ApplicationPreviewModal item={selectedApplication} onClose={() => setSelectedApplication(null)} />

      {marksData ? (
        <div style={overlayStyle} onClick={() => setMarksData(null)}>
          <div style={modalCardStyle} onClick={(event) => event.stopPropagation()}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: "12px", marginBottom: "18px", flexWrap: "wrap" }}>
              <div>
                <h3 style={{ margin: "0 0 6px", color: "#0f172a" }}>Marks Entry</h3>
                <p style={{ margin: 0, color: "#64748b" }}>
                  {marksData.application?.student_name} - {marksData.application?.course_name || "No course"}
                </p>
              </div>
              <button type="button" className="btn btn-outline-secondary" onClick={() => setMarksData(null)}>
                Close
              </button>
            </div>

            {marksLoading ? (
              <p>Loading marks data...</p>
            ) : marksData.rows.length === 0 ? (
              <div style={statCard}>No syllabus or course module found for this student's selected course.</div>
            ) : (
              <>
                <div style={tableWrap}>
                  <table style={tableStyle}>
                    <thead>
                      <tr>
                        <th style={thStyle}>Semester</th>
                        <th style={thStyle}>Module / Syllabus</th>
                        <th style={thStyle}>Marks Obtained</th>
                        <th style={thStyle}>Max Marks</th>
                        <th style={thStyle}>Approval Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {marksData.rows.map((row) => (
                        <tr key={row.module_id}>
                          <td style={tdStyle}>{row.semester_title || `Semester ${row.semester_no || 1}`}</td>
                          <td style={tdStyle}>{row.module_title}</td>
                          <td style={tdStyle}>
                            <input
                              className="form-control"
                              type="number"
                              value={row.marks_obtained}
                              onChange={(event) => updateMarksRow(row.module_id, "marks_obtained", event.target.value)}
                            />
                          </td>
                          <td style={tdStyle}>
                            <input className="form-control" type="number" value={row.max_marks} onChange={(event) => updateMarksRow(row.module_id, "max_marks", event.target.value)} />
                          </td>
                          <td style={tdStyle}><StatusChip status={row.approval_status} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div style={{ marginTop: "16px", display: "flex", gap: "10px", flexWrap: "wrap" }}>
                  <button type="button" className="btn btn-primary" onClick={submitMarks} disabled={marksSaving}>
                    {marksSaving ? "Submitting..." : "Send To Master Admin"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

export function MyWallet() {
    const { data } = usePortalData((id) => `/franchise-portal/${id}/wallet`, {
      balance: 0,
      wallets: [
        { key: "main_wallet", label: "Wallet Balance", balance: 0 },
        { key: "tools_wallet", label: "Tools Point", balance: 0 },
        { key: "gift_card_wallet", label: "Gift Card", balance: 0 },
        { key: "reserve_wallet", label: "Skill Point", balance: 0 },
      ],
      transactions: [],
    });
    return (
      <div>
        <ModuleHeader title="My Wallet" description="Franchise panel now supports Wallet Balance, Tools Point, Gift Card, and Skill Point in one place." />
        <div style={cardGrid}>
          {(data.wallets || []).map((wallet) => (
            <div key={wallet.key} style={statCard}>
              <p style={{ margin: 0, color: '#64748b', fontWeight: 700 }}>{walletLabel(wallet.label, wallet.key)}</p>
              <h2 style={{ marginBottom: 0 }}>
                {wallet.key === "gift_card_wallet" || wallet.key === "reserve_wallet" || wallet.key === "tools_wallet"
                  ? Number(wallet.balance || 0).toFixed(2)
                  : `Rs ${Number(wallet.balance || 0).toFixed(2)}`}
              </h2>
            </div>
          ))}
        <div style={statCard}><p style={{ margin: 0, color: '#64748b', fontWeight: 700 }}>Transactions</p><h2 style={{ marginBottom: 0 }}>{data.transactions?.length || 0}</h2></div>
      </div>
      <div style={{ ...statCard, marginTop: '16px' }}>Wallet add/deduct UI can be connected next. Multi-wallet data structure is now ready from the backend.</div>
    </div>
  );
}

export function LoadMoney() {
  const { franchise } = useOutletContext();
  const initialForm = {
    amount: "",
    payment_mode: "",
    reference_no: "",
    note: "",
    receipt_file: null,
  };
  const [form, setForm] = useState({
    ...initialForm,
  });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [fileInputKey, setFileInputKey] = useState(0);
  const [receiptPreview, setReceiptPreview] = useState(null);

  const loadRows = useCallback(async () => {
    if (!franchise?.id) return;
    try {
      setLoading(true);
      const res = await axios.get(`${API}/franchise-portal/${franchise.id}/load-money-requests`, {
        params: { _ts: Date.now() },
      });
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [franchise?.id]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const handleSubmit = async () => {
    if (!form.amount || Number(form.amount) <= 0) {
      setMessage("Enter a valid amount");
      return;
    }

    if (!form.payment_mode) {
      setMessage("Select payment mode");
      return;
    }

    try {
      setSaving(true);
      setMessage("");
      const formData = new FormData();
      formData.append("amount", form.amount);
      formData.append("payment_mode", form.payment_mode);
      formData.append("reference_no", form.reference_no);
      formData.append("note", form.note);
      if (form.receipt_file) {
        formData.append("receipt_file", form.receipt_file);
      }

      await axios.post(`${API}/franchise-portal/${franchise.id}/load-money-requests`, formData);
      setForm({ ...initialForm });
      setFileInputKey((prev) => prev + 1);
      setMessage("Load money request submitted successfully");
      await loadRows();
    } catch (error) {
      setMessage(getErrorMessage(error, "Load money request failed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <ModuleHeader title="Load Wallet" description="Add wallet request flow for the franchise wallet can be managed from here." />
      <div style={{ ...statCard, display: "grid", gap: "14px", marginBottom: "16px" }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "14px" }}>
          <div>
            <label className="form-label fw-semibold">Amount</label>
            <input className="form-control" type="number" min="1" value={form.amount} onChange={(event) => setForm((prev) => ({ ...prev, amount: event.target.value }))} />
          </div>
          <div>
            <label className="form-label fw-semibold">Payment Mode</label>
            <select className="form-select" value={form.payment_mode} onChange={(event) => setForm((prev) => ({ ...prev, payment_mode: event.target.value }))}>
              <option value="">Select Payment Mode</option>
              <option value="bank_transfer">Bank Transfer</option>
              <option value="upi">UPI</option>
              <option value="cash_deposit">Cash Deposit</option>
              <option value="online_payment">Online Payment</option>
            </select>
          </div>
          <div>
            <label className="form-label fw-semibold">Reference No</label>
            <input className="form-control" value={form.reference_no} onChange={(event) => setForm((prev) => ({ ...prev, reference_no: event.target.value }))} />
          </div>
          <div>
            <label className="form-label fw-semibold">Money Transfer Receipt</label>
            <input key={fileInputKey} type="file" className="form-control" accept=".jpg,.jpeg,.jfif,.png,.pdf,.webp" onChange={(event) => setForm((prev) => ({ ...prev, receipt_file: event.target.files?.[0] || null }))} />
          </div>
        </div>
        <div>
          <label className="form-label fw-semibold">Note</label>
          <input className="form-control" value={form.note} onChange={(event) => setForm((prev) => ({ ...prev, note: event.target.value }))} />
        </div>
        <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", alignItems: "center" }}>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={saving}
            style={{
              border: "none",
              borderRadius: "12px",
              padding: "10px 18px",
              fontWeight: 700,
              color: "#ffffff",
              background: "linear-gradient(135deg, #7c2d3a 0%, #c2410c 100%)",
              boxShadow: "0 14px 24px rgba(124, 45, 58, 0.22)",
              transition: "0.2s ease",
            }}
            onMouseEnter={(event) => {
              if (!saving) {
                event.currentTarget.style.background = "linear-gradient(135deg, #4e1b24 0%, #7c2d3a 100%)";
              }
            }}
            onMouseLeave={(event) => {
              if (!saving) {
                event.currentTarget.style.background = "linear-gradient(135deg, #7c2d3a 0%, #c2410c 100%)";
              }
            }}
          >
            {saving ? "Submitting..." : "Send Request"}
          </button>
          {message ? <span style={{ color: "#7c2d3a", fontWeight: 600 }}>{message}</span> : null}
        </div>
      </div>

      <div style={statCard}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: "12px", marginBottom: "14px", flexWrap: "wrap" }}>
          <div>
            <h3 style={{ margin: 0, color: "#0f172a" }}>Request History</h3>
            <p style={{ margin: "6px 0 0", color: "#64748b" }}>All load wallet requests sent from this franchise account.</p>
          </div>
        </div>

        <div style={tableWrap}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Amount</th>
                <th style={thStyle}>Payment Mode</th>
                <th style={thStyle}>Reference</th>
                <th style={thStyle}>Receipt</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>Note</th>
                <th style={thStyle}>Date</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td style={tdStyle} colSpan={7}>Loading...</td>
                </tr>
              ) : rows.length ? (
                rows.map((row) => (
                  <tr key={row.id}>
                    <td style={tdStyle}>Rs {Number(row.amount || 0).toFixed(2)}</td>
                    <td style={{ ...tdStyle, textTransform: "capitalize" }}>{String(row.payment_mode || "-").replaceAll("_", " ")}</td>
                    <td style={tdStyle}>{row.reference_no || "-"}</td>
                    <td style={tdStyle}>
                      {row.receipt_file ? (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() =>
                            setReceiptPreview({
                              url: `${uploadsBase}/load-money-receipts/${row.receipt_file}`,
                              name: row.receipt_file,
                            })
                          }
                        >
                          <i className="bi bi-eye-fill"></i>
                        </button>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td style={tdStyle}><StatusChip status={row.status} /></td>
                    <td style={tdStyle}>{row.admin_note || row.note || "-"}</td>
                    <td style={tdStyle}>{formatKolkataDateTime(row.created_at)}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td style={tdStyle} colSpan={7}>No load wallet requests found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {receiptPreview ? (
        <div style={overlayStyle} onClick={() => setReceiptPreview(null)}>
          <div style={{ ...modalCardStyle, width: "min(640px, 100%)", padding: "16px" }} onClick={(event) => event.stopPropagation()}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px", marginBottom: "14px" }}>
              <h3 style={{ margin: 0, color: "#0f172a" }}>Receipt Preview</h3>
              <button type="button" className="btn btn-sm btn-danger" onClick={() => setReceiptPreview(null)}>
                Close
              </button>
            </div>
            {receiptPreview.url.toLowerCase().endsWith(".pdf") ? (
              <iframe title="Receipt Preview" src={receiptPreview.url} style={{ width: "100%", height: "62vh", border: "none", borderRadius: "14px" }} />
            ) : (
              <img src={receiptPreview.url} alt="Receipt Preview" style={{ width: "100%", maxHeight: "62vh", objectFit: "contain", borderRadius: "14px" }} />
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

export function PostalOrder() {
  const { franchise } = useOutletContext();
  const [requirement, setRequirement] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState("");

  const loadRows = useCallback(async () => {
    if (!franchise?.id) return;
    try {
      setLoading(true);
      const res = await axios.get(`${API}/franchise-portal/${franchise.id}/postal-orders`, {
        params: { _ts: Date.now() },
      });
      setRows(res.data || []);
    } catch (error) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [franchise?.id]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const handleSend = async () => {
    if (!requirement.trim()) {
      setMessage("Requirement লিখতে হবে");
      return;
    }

    try {
      setSending(true);
      setMessage("");
      await axios.post(`${API}/franchise-portal/${franchise.id}/postal-orders`, {
        requirement,
      });
      setRequirement("");
      setMessage("Postal order sent successfully");
      await loadRows();
    } catch (error) {
      setMessage(error.response?.data?.message || "Postal order send failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      <ModuleHeader title="Postal Order" description="Certificate বা অন্য requirement franchise panel থেকে পাঠালে সেটা instant main admin postal order panel-এ চলে যাবে।" />

      <div style={{ ...statCard, display: "grid", gap: "14px" }}>
        <label style={{ color: "#475569", fontWeight: 700 }}>Requirement</label>
        <textarea
          className="form-control"
          rows="5"
          placeholder="Write requirement details here"
          value={requirement}
          onChange={(event) => setRequirement(event.target.value)}
        />
        <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", alignItems: "center" }}>
          <button type="button" className="btn btn-primary" onClick={handleSend} disabled={sending}>
            {sending ? "Sending..." : "Send"}
          </button>
          {message ? <span style={{ color: message.toLowerCase().includes("failed") ? "#b91c1c" : "#166534", fontWeight: 700 }}>{message}</span> : null}
        </div>
      </div>

      <div style={{ ...statCard, marginTop: "16px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: "12px", flexWrap: "wrap", marginBottom: "16px" }}>
          <div>
            <h3 style={{ margin: "0 0 6px", color: "#0f172a" }}>Postal Order List</h3>
            <p style={{ margin: 0, color: "#64748b" }}>Your franchise postal order requests will grow serial-wise below.</p>
          </div>
          <button type="button" className="btn btn-outline-secondary" onClick={loadRows}>Refresh</button>
        </div>

        <div style={tableWrap}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Sl No</th>
                <th style={thStyle}>Requirement</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>Remarks</th>
              </tr>
            </thead>
            <tbody>
              {!loading && rows.map((row, index) => (
                <tr key={row.id}>
                  <td style={tdStyle}>{index + 1}</td>
                  <td style={tdStyle}>{row.requirement}</td>
                  <td style={tdStyle}><StatusChip status={row.status} /></td>
                  <td style={tdStyle}>
                    {row.status_remark || row.dispatch_remarks || "-"}
                  </td>
                </tr>
              ))}
              {!loading && rows.length === 0 ? (
                <tr>
                  <td style={tdStyle} colSpan="4">No postal order sent yet.</td>
                </tr>
              ) : null}
              {loading ? (
                <tr>
                  <td style={tdStyle} colSpan="4">Loading postal orders...</td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export function PostalDispatch() {
  const { franchise } = useOutletContext();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadRows = useCallback(async () => {
    if (!franchise?.id) return;
    try {
      setLoading(true);
      const res = await axios.get(`${API}/franchise-portal/${franchise.id}/postal-dispatch`, {
        params: { _ts: Date.now() },
      });
      setRows(res.data || []);
    } catch (error) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [franchise?.id]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  return (
    <div>
      <ModuleHeader title="Postal Dispatch" description="Admin approved postal order rows এখানে automatically dispatch list-এ show হবে." />
      <div style={{ ...statCard, marginTop: "16px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: "12px", flexWrap: "wrap", marginBottom: "16px" }}>
          <div>
            <h3 style={{ margin: "0 0 6px", color: "#0f172a" }}>Postal Dispatch List</h3>
            <p style={{ margin: 0, color: "#64748b" }}>Approved dispatch item-গুলো tracking link-সহ এখানে থাকবে.</p>
          </div>
          <button type="button" className="btn btn-outline-secondary" onClick={loadRows}>Refresh</button>
        </div>

        <div style={tableWrap}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={{ ...thStyle, ...dividerCellStyle, ...centerCellStyle, ...autoWidthCellStyle }}>Order Date</th>
                <th style={{ ...thStyle, ...dividerCellStyle }}>Requirement</th>
                <th style={{ ...thStyle, ...dividerCellStyle }}>Dispatch Remarks</th>
                <th style={{ ...thStyle, ...dividerCellStyle, ...autoWidthCellStyle }}>Action</th>
                <th style={{ ...thStyle, ...dividerCellStyle, ...autoWidthCellStyle }}>Dispatch Date</th>
                <th style={{ ...thStyle, ...autoWidthCellStyle }}>Location</th>
              </tr>
            </thead>
            <tbody>
              {!loading && rows.map((row, index) => (
                <tr key={row.id}>
                  <td style={{ ...tdStyle, ...dividerCellStyle, ...centerCellStyle, ...autoWidthCellStyle }}>{formatKolkataDate(row.created_at)}</td>
                  <td style={{ ...tdStyle, ...dividerCellStyle }}>{row.requirement}</td>
                  <td style={{ ...tdStyle, ...dividerCellStyle }}>{row.dispatch_remarks || "-"}</td>
                  <td style={{ ...tdStyle, ...dividerCellStyle, ...centerCellStyle, ...autoWidthCellStyle }}>
                    <div style={{ display: "grid", gap: "6px" }}>
                      <div>
                        <StatusChip status={row.status} />
                      </div>
                      {row.status === "hold" || row.status === "rejected" ? (
                        <div style={{ color: "#64748b", fontSize: "12px", lineHeight: 1.5 }}>
                          {row.status_remark || "-"}
                        </div>
                      ) : null}
                    </div>
                  </td>
                  <td style={{ ...tdStyle, ...dividerCellStyle, ...centerCellStyle, ...autoWidthCellStyle }}>{formatKolkataDate(row.approved_at)}</td>
                  <td style={{ ...tdStyle, ...centerCellStyle, ...autoWidthCellStyle }}>
                    {row.tracking_link ? (
                      <a href={row.tracking_link} target="_blank" rel="noreferrer" className="btn btn-outline-primary btn-sm">
                        <i className="bi bi-geo-alt-fill"></i>
                      </a>
                    ) : (
                      "-"
                    )}
                  </td>
                </tr>
              ))}
              {!loading && rows.length === 0 ? (
                <tr>
                  <td style={tdStyle} colSpan="6">No postal dispatch available yet.</td>
                </tr>
              ) : null}
              {loading ? (
                <tr>
                  <td style={tdStyle} colSpan="6">Loading postal dispatch...</td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export function ViewStatement() {
  const { data: rows } = usePortalData((id) => `/franchise-portal/${id}/statement`, []);
  const [filters, setFilters] = useState({ fromDate: "", toDate: "" });
  const exportRef = useRef(null);
  const filteredRows = useMemo(() => filterStatementRows(rows, filters), [rows, filters]);
  const summary = useMemo(() => {
    const totals = buildStatementSummary(filteredRows);
    return {
      ...totals,
      net: totals.totalCredit - totals.totalDebit,
    };
  }, [filteredRows]);

  return (
    <div>
      <ModuleHeader title="View Statement" description="Every wallet transaction is listed here with cleaner wallet names, proper references, date-only view, filter, and export." />
      <div style={{ ...statCard, display: "grid", gap: "12px", marginBottom: "16px" }}>
        <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", gap: "12px", alignItems: "end" }}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "12px" }}>
            <div>
              <label style={{ display: "block", marginBottom: "6px", fontSize: "12px", fontWeight: 700, color: "#64748b" }}>From Date</label>
              <input
                type="date"
                value={filters.fromDate}
                onChange={(e) => setFilters((prev) => ({ ...prev, fromDate: e.target.value }))}
                style={{ border: "1px solid #cbd5e1", borderRadius: "12px", padding: "10px 12px", fontSize: "13px", minWidth: "180px" }}
              />
            </div>
            <div>
              <label style={{ display: "block", marginBottom: "6px", fontSize: "12px", fontWeight: 700, color: "#64748b" }}>To Date</label>
              <input
                type="date"
                value={filters.toDate}
                onChange={(e) => setFilters((prev) => ({ ...prev, toDate: e.target.value }))}
                style={{ border: "1px solid #cbd5e1", borderRadius: "12px", padding: "10px 12px", fontSize: "13px", minWidth: "180px" }}
              />
            </div>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
            <button
              type="button"
              onClick={() => downloadStatementCsv({ fileName: "franchise-statement.csv", rows: filteredRows, summary, filters })}
              style={{ border: "1px solid #cbd5e1", background: "#fff", borderRadius: "12px", padding: "10px 14px", fontSize: "12px", fontWeight: 700 }}
            >
              CSV
            </button>
            <button
              type="button"
              onClick={() => downloadStatementExcel({ fileName: "franchise-statement.xls", rows: filteredRows, summary, filters })}
              style={{ border: "1px solid #cbd5e1", background: "#fff", borderRadius: "12px", padding: "10px 14px", fontSize: "12px", fontWeight: 700 }}
            >
              Excel
            </button>
            <button
              type="button"
              onClick={() => downloadStatementPdf({ fileName: "franchise-statement.pdf", element: exportRef.current })}
              style={{ border: "1px solid #cbd5e1", background: "#fff", borderRadius: "12px", padding: "10px 14px", fontSize: "12px", fontWeight: 700 }}
            >
              PDF
            </button>
            <button
              type="button"
              onClick={() => setFilters({ fromDate: "", toDate: "" })}
              style={{ border: "1px solid #cbd5e1", background: "#f8fafc", borderRadius: "12px", padding: "10px 14px", fontSize: "12px", fontWeight: 700 }}
            >
              Clear Filter
            </button>
          </div>
        </div>
      </div>
      <div ref={exportRef}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "16px", marginBottom: "16px" }}>
        <div style={statCard}><p style={{ margin: 0, color: "#64748b", fontWeight: 700, fontSize: "12px" }}>Total Credit</p><h3 style={{ margin: "10px 0 0", fontSize: "24px" }}>Rs {summary.totalCredit.toFixed(2)}</h3></div>
        <div style={statCard}><p style={{ margin: 0, color: "#64748b", fontWeight: 700, fontSize: "12px" }}>Total Debit</p><h3 style={{ margin: "10px 0 0", fontSize: "24px" }}>Rs {summary.totalDebit.toFixed(2)}</h3></div>
        <div style={statCard}><p style={{ margin: 0, color: "#64748b", fontWeight: 700, fontSize: "12px" }}>Net Movement</p><h3 style={{ margin: "10px 0 0", fontSize: "24px" }}>Rs {summary.net.toFixed(2)}</h3></div>
        <div style={statCard}><p style={{ margin: 0, color: "#64748b", fontWeight: 700, fontSize: "12px" }}>Total Point Credit</p><h3 style={{ margin: "10px 0 0", fontSize: "24px" }}>{summary.totalPointCredit.toFixed(2)} Pt</h3></div>
      </div>
      <div style={tableWrap}>
        <table style={{ ...tableStyle, fontSize: "13px" }}>
          <thead>
            <tr>
              <th style={thStyle}>Date</th>
              <th style={thStyle}>Wallet</th>
              <th style={thStyle}>Type</th>
              <th style={thStyle}>Amount</th>
              <th style={thStyle}>Purpose</th>
              <th style={thStyle}>Reference</th>
            </tr>
          </thead>
          <tbody>
            {filteredRows.length ? filteredRows.map((row) => (
              <tr key={row.id}>
                <td style={tdStyle}>{formatStatementDate(row.created_at)}</td>
                <td style={tdStyle}>{formatWalletName(row.wallet_type)}</td>
                <td style={{ ...tdStyle, textTransform: "capitalize", fontWeight: 700, color: row.transaction_type === "credit" ? "#166534" : "#b91c1c" }}>{row.transaction_type}</td>
                <td style={tdStyle}>{formatStatementAmount(row.amount, row.wallet_type)}</td>
                <td style={{ ...tdStyle, fontSize: "12px", lineHeight: 1.5 }}>{formatStatementPurpose(row)}</td>
                <td style={tdStyle}>{formatStatementReference(row)}</td>
              </tr>
            )) : (
              <tr>
                <td style={tdStyle} colSpan={6}>No transaction found.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      </div>
      </div>
    );
  }

export function ExamList() {
  const { data: exams } = usePortalData((id) => `/franchise-portal/${id}/exams`, []);
  return (
    <div>
      <ModuleHeader title="New Exam Create and View Total Exam" description="This page is ready for exam creation and shows total exams in list form." />
      <div style={tableWrap}><table style={tableStyle}><thead><tr><th style={thStyle}>Exam</th><th style={thStyle}>Questions</th><th style={thStyle}>Results</th></tr></thead><tbody>
        {exams.map((exam) => <tr key={exam.id}><td style={tdStyle}>{exam.title}</td><td style={tdStyle}>{exam.question_count}</td><td style={tdStyle}>{exam.result_count}</td></tr>)}
      </tbody></table></div>
    </div>
  );
}

export function ExamQuestions() {
  const { data: exams } = usePortalData((id) => `/franchise-portal/${id}/exams`, []);
  return (
    <div>
      <ModuleHeader title="MCQ Question View and Add also CSV Upload" description="Exam selection, question entry, and CSV upload placeholders are ready. Backend question table is also created." />
      <div style={cardGrid}>
        {exams.map((exam) => <div key={exam.id} style={statCard}><h3 style={{ marginTop: 0 }}>{exam.title}</h3><p style={{ margin: 0, color: '#64748b' }}>Questions: {exam.question_count}</p><p style={{ margin: '10px 0 0', color: '#1d4ed8' }}>CSV upload ready for next step</p></div>)}
      </div>
    </div>
  );
}

export function OnlineExamResult() {
  const { data: results } = usePortalData((id) => `/franchise-portal/${id}/results`, []);
  return (
    <div>
      <ModuleHeader title="Online Exam Result" description="Online exam result rows submitted for this franchise." />
      <div style={tableWrap}><table style={tableStyle}><thead><tr><th style={thStyle}>Student</th><th style={thStyle}>Exam</th><th style={thStyle}>Score</th><th style={thStyle}>Status</th></tr></thead><tbody>
        {results.map((row) => <tr key={row.id}><td style={tdStyle}>{row.student_name}</td><td style={tdStyle}>{row.exam_title || '-'}</td><td style={tdStyle}>{row.score}/{row.total_questions}</td><td style={tdStyle}>{row.result_status}</td></tr>)}
      </tbody></table></div>
    </div>
  );
}

function CertificationTable({ title, description, rows }) {
  const filteredRows = Array.isArray(rows) ? rows : [];

  const printCertificate = async (row) => {
    const design = await fetchAmciDesign("certificate");
    const marksRes = await axios.get(`${API}/student-applications/${row.student_id || row.id}/marks`).catch(() => ({ data: { rows: [] } }));
    const marksRows = Array.isArray(marksRes.data?.rows) ? marksRes.data.rows : [];
    const summary = buildMarksSummary(marksRows);
    const gradeRows = await fetchGradePoints();
    const gradeInfo = resolveGradePointByPercentage(summary.percentage, gradeRows);
    printAmciDocument("certificate", design, {
      student_name: row.student_name,
      father_name: row.father_name,
      registration_no: row.registration_no,
      dob: row.dob,
      course_name: row.course_name,
      admission_date: row.admission_date,
      course_completion_date: row.course_completion_date,
      study_centre: row.study_centre_name || "Franchise Centre",
      centre_name: row.study_centre_name || "Franchise Centre",
      centre_code: row.study_centre_login || row.centre_code || "",
      centre_address: row.study_centre_address || row.centre_address || row.franchise_address || "",
      session_year: row.session_year,
      certificate_no: row.application_no || row.registration_no,
      issue_date: row.last_downloaded_at || "",
      grade: gradeInfo.grade,
      grade_point: gradeInfo.grade_point,
      percentage: `${summary.percentage}%`,
      total_obtained: summary.totalObtained,
      total_full_marks: summary.totalFull,
      verification_qr: row.registration_no || "Verification QR",
      student_photo: resolveStudentPhoto(row),
    });
  };

  const printMarksheet = async (row) => {
    try {
      const res = await axios.get(`${API}/student-applications/${row.student_id || row.id}/marks`);
      const marksRows = Array.isArray(res.data?.rows) ? res.data.rows : [];
      const summary = buildMarksSummary(marksRows);
      const design = await fetchAmciDesign("marksheet");
      const gradeRows = await fetchGradePoints();
      const gradeInfo = resolveGradePointByPercentage(summary.percentage, gradeRows);
      printAmciDocument("marksheet", design, {
        student_name: row.student_name,
        registration_no: row.registration_no,
        dob: row.dob,
        course_name: row.course_name,
        admission_date: row.admission_date,
        course_completion_date: row.course_completion_date,
        study_centre: row.study_centre_name || "Franchise Centre",
        centre_name: row.study_centre_name || "Franchise Centre",
        centre_code: row.study_centre_login || row.centre_code || "",
        module_details: buildModuleDetailsRows(marksRows),
        total_obtained: summary.totalObtained,
        total_full_marks: summary.totalFull,
        total_pass_marks: summary.totalPass,
        obtained_marks: summary.totalObtained,
        theory_obtained_marks: summary.theoryObtained,
        theory_full_marks: summary.theoryFull,
        theory_pass_marks: summary.theoryPass,
        practical_obtained_marks: summary.practicalObtained,
        practical_full_marks: summary.practicalFull,
        practical_pass_marks: summary.practicalPass,
        project_assignment_obtained_marks: summary.projectObtained,
        project_assignment_full_marks: summary.projectFull,
        project_assignment_pass_marks: summary.projectPass,
        viva_obtained_marks: summary.vivaObtained,
        viva_full_marks: summary.vivaFull,
        viva_pass_marks: summary.vivaPass,
        percentage: `${summary.percentage}%`,
        grade: gradeInfo.grade,
        issue_date: row.last_downloaded_at || "",
        student_photo: resolveStudentPhoto(row),
      });
    } catch (error) {
      window.alert(getErrorMessage(error, "Marksheet data load failed"));
    }
  };

  return (
    <div>
      <ModuleHeader title={title} description={description} />
      <div style={tableWrap}>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Student</th>
              <th style={thStyle}>Registration No</th>
              <th style={thStyle}>Course</th>
              <th style={thStyle}>Updated</th>
              <th style={thStyle}>Action</th>
            </tr>
          </thead>
          <tbody>
            {filteredRows.length ? filteredRows.map((row) => (
              <tr key={row.id}>
                <td style={tdStyle}>
                  <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                    <img
                      src={row.photo ? fileUrl(row.photo) : "https://placehold.co/52x52/e2e8f0/475569?text=ST"}
                      alt={row.student_name}
                      style={{ width: "52px", height: "52px", borderRadius: "16px", objectFit: "cover", border: "1px solid #e2e8f0" }}
                    />
                    <div>
                      <div style={{ fontWeight: 700 }}>{row.student_name}</div>
                      <div style={{ color: "#64748b", fontSize: "12px" }}>{row.application_no || "-"}</div>
                    </div>
                  </div>
                </td>
                <td style={tdStyle}>{row.registration_no || "-"}</td>
                <td style={tdStyle}>{row.course_name || "-"}</td>
                <td style={tdStyle}>{formatKolkataDateTime(row.last_downloaded_at) || "-"}</td>
                <td style={tdStyle}>
                  <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                    <button type="button" className="btn btn-sm btn-primary" onClick={() => printMarksheet(row)}>
                      Marksheet Download
                    </button>
                    <button type="button" className="btn btn-sm btn-success" onClick={() => printCertificate(row)}>
                      Certificate Download
                    </button>
                  </div>
                </td>
              </tr>
            )) : (
              <tr>
                <td style={tdStyle} colSpan={5}>No certified student found.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function MarksheetDownload() {
  const { data: rows } = usePortalData((id) => `/franchise-portal/${id}/certifications`, []);
  return <CertificationTable title="Marksheet Download" description="Certified students move here after exam approval. Download printable marksheet or certificate from the action buttons." rows={rows} />;
}

export function CertificateDownload() {
  const { data: rows } = usePortalData((id) => `/franchise-portal/${id}/certifications`, []);
  return <CertificationTable title="Certificate Download" description="Certified students are available here with printable marksheet and certificate actions." rows={rows} />;
}

export function CertifiedStudents() {
  const { data: rows } = usePortalData((id) => `/franchise-portal/${id}/certifications`, []);
  const uniqueRows = (rows || []).reduce((accumulator, row) => {
    const key = String(row.student_id || row.id || "");
    if (!accumulator.some((item) => String(item.student_id || item.id || "") === key)) {
      accumulator.push(row);
    }
    return accumulator;
  }, []);

  return <CertificationTable title="Certified Student" description="Only certified students are shown with marksheet and certificate download actions." rows={uniqueRows} />;
}

export function FranchiseTraining() {
  const { franchise } = useOutletContext();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [viewportWidth, setViewportWidth] = useState(() => (typeof window !== "undefined" ? window.innerWidth : 1280));

  useEffect(() => {
    let active = true;
    const loadRows = async () => {
      try {
        const res = await axios.get(`${API}/franchise-training-videos`, { params: { franchise_id: franchise?.id, _ts: Date.now() } });
        if (active) setRows(Array.isArray(res.data) ? res.data : []);
      } catch (error) {
        if (active) setRows([]);
      } finally {
        if (active) setLoading(false);
      }
    };

    loadRows();
    return () => {
      active = false;
    };
  }, [franchise?.id]);

  useEffect(() => {
    const handleResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const embedUrl = (url) => {
    const value = String(url || "").trim();
    const embedMatch = /youtube(?:-nocookie)?\.com\/embed\/([^?&/]+)/i.exec(value);
    const watchMatch = /(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&?/]+)/i.exec(value);
    const videoId = embedMatch?.[1] || watchMatch?.[1];
    if (videoId) {
      return `https://www.youtube-nocookie.com/embed/${videoId}?autoplay=0&controls=1&modestbranding=1&rel=0&iv_load_policy=3&fs=0&playsinline=1&cc_load_policy=0&showinfo=0&disablekb=0`;
    }
    return value;
  };

  const gridTemplateColumns = viewportWidth <= 768 ? "1fr" : viewportWidth <= 1100 ? "repeat(2, minmax(0, 1fr))" : "repeat(3, minmax(0, 1fr))";

  return (
    <div>
      <ModuleHeader title="Franchise Training Section" description="Training videos uploaded from admin panel will appear here for franchise viewing." />
      <div style={{ display: "grid", gridTemplateColumns, gap: "16px" }}>
        {!loading && rows.map((row) => (
          <div key={row.id} style={{ ...statCard, padding: 0, overflow: "hidden" }}>
            <button
              type="button"
              onClick={() => setSelectedVideo(row)}
              style={{ border: "none", padding: 0, width: "100%", background: "transparent", cursor: "pointer" }}
            >
              <img
                src={row.thumbnail ? `${API.replace('/api', '')}/uploads/training-thumbnails/${row.thumbnail}` : "https://placehold.co/640x360/e2e8f0/475569?text=Training"}
                alt={row.title || "Training thumbnail"}
                style={{ width: "100%", aspectRatio: "16 / 9", objectFit: "cover", borderBottom: "1px solid rgba(148,163,184,.18)" }}
              />
            </button>
            <div style={{ padding: "18px" }}>
              <h4 style={{ margin: 0, color: "#0f172a" }}>{row.title}</h4>
            </div>
          </div>
        ))}
        {!loading && rows.length === 0 ? <div style={statCard}>No training video uploaded yet.</div> : null}
        {loading ? <div style={statCard}>Loading training videos...</div> : null}
      </div>

      {selectedVideo ? (
        <div
          style={{ position: "fixed", inset: 0, background: "rgba(15, 23, 42, 0.72)", display: "flex", alignItems: "center", justifyContent: "center", padding: "18px", zIndex: 4000 }}
          onClick={() => setSelectedVideo(null)}
        >
          <div
            style={{ width: "min(960px, 100%)", background: "#ffffff", borderRadius: "24px", padding: "18px", boxShadow: "0 28px 70px rgba(15,23,42,0.26)" }}
            onClick={(event) => event.stopPropagation()}
          >
            <div style={{ display: "flex", justifyContent: "space-between", gap: "14px", alignItems: "center", marginBottom: "14px", flexWrap: "wrap" }}>
              <div>
                <h3 style={{ margin: 0, color: "#0f172a" }}>{selectedVideo.title}</h3>
              </div>
              <button
                type="button"
                className="btn btn-danger"
                onClick={() => setSelectedVideo(null)}
              >
                Close
              </button>
            </div>
            <div style={{ width: "100%", aspectRatio: "16 / 9", borderRadius: "18px", overflow: "hidden", border: "1px solid rgba(148,163,184,.18)", background: "#000" }}>
              <iframe
                title={selectedVideo.title}
                src={embedUrl(selectedVideo.video_url)}
                style={{ width: "100%", height: "100%", border: "none" }}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              />
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export function TicketBox() {
  const { franchise } = useOutletContext();
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ subject: "", message: "" });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  const loadRows = useCallback(async () => {
    if (!franchise?.id) return;
    try {
      const res = await axios.get(`${API}/franchise-tickets`, { params: { franchise_id: franchise.id, _ts: Date.now() } });
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      setRows([]);
    }
  }, [franchise?.id]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const handleSubmit = async () => {
    if (!form.subject.trim() || !form.message.trim()) {
      setMessage("Subject and message are required");
      return;
    }

    try {
      setSaving(true);
      setMessage("");
      await axios.post(`${API}/franchise-tickets`, {
        franchise_id: franchise.id,
        subject: form.subject,
        message: form.message,
      });
      setForm({ subject: "", message: "" });
      setMessage("Ticket created successfully");
      await loadRows();
    } catch (error) {
      setMessage(error.response?.data?.message || "Ticket create failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ display: "grid", gap: "18px" }}>
      <ModuleHeader title="Ticket Box" description="Franchise and admin communication tickets can be raised and tracked from here." />
      <div style={{ ...statCard, display: "grid", gap: "12px" }}>
        <input className="form-control" placeholder="Ticket Subject" value={form.subject} onChange={(e) => setForm((prev) => ({ ...prev, subject: e.target.value }))} />
        <textarea className="form-control" rows="5" placeholder="Write ticket message here" value={form.message} onChange={(e) => setForm((prev) => ({ ...prev, message: e.target.value }))}></textarea>
        <div style={{ display: "flex", gap: "12px", alignItems: "center", flexWrap: "wrap" }}>
          <button type="button" className="btn btn-primary" onClick={handleSubmit} disabled={saving}>
            {saving ? "Sending..." : "Create Ticket"}
          </button>
          {message ? <span style={{ color: message.toLowerCase().includes("failed") ? "#b91c1c" : "#166534", fontWeight: 700 }}>{message}</span> : null}
        </div>
      </div>

      <div style={tableWrap}>
        <table style={{ ...tableStyle, border: "1px solid rgba(148, 163, 184, 0.18)", borderRadius: "14px", overflow: "hidden" }}>
          <thead>
            <tr>
              <th style={{ ...thStyle, borderRight: "1px solid rgba(148, 163, 184, 0.18)" }}>Subject</th>
              <th style={{ ...thStyle, borderRight: "1px solid rgba(148, 163, 184, 0.18)" }}>Message</th>
              <th style={{ ...thStyle, borderRight: "1px solid rgba(148, 163, 184, 0.18)" }}>Status</th>
              <th style={thStyle}>Admin Reply</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                <td style={{ ...tdStyle, borderRight: "1px solid rgba(148, 163, 184, 0.12)", fontWeight: 800, color: "#0f172a", textAlign: "center", verticalAlign: "middle" }}>{row.subject}</td>
                <td style={{ ...tdStyle, borderRight: "1px solid rgba(148, 163, 184, 0.12)", textAlign: "center", verticalAlign: "middle" }}>{row.message}</td>
                <td style={{ ...tdStyle, borderRight: "1px solid rgba(148, 163, 184, 0.12)", textAlign: "center", verticalAlign: "middle" }}>
                  <span style={{
                    display: "inline-flex",
                    padding: "6px 10px",
                    borderRadius: "999px",
                    fontWeight: 800,
                    textTransform: "capitalize",
                    background: row.status === "closed" ? "#dcfce7" : row.status === "in_progress" ? "#dbeafe" : "#fef3c7",
                    color: row.status === "closed" ? "#166534" : row.status === "in_progress" ? "#1d4ed8" : "#92400e",
                  }}>
                    {String(row.status || "open").replaceAll("_", " ")}
                  </span>
                </td>
                <td style={{ ...tdStyle, color: row.admin_reply ? "#0f172a" : "#94a3b8", textAlign: "center", verticalAlign: "middle" }}>{row.admin_reply || "Waiting for admin reply"}</td>
              </tr>
            ))}
            {rows.length === 0 ? (
              <tr>
                <td style={tdStyle} colSpan={4}>No ticket created yet.</td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function ProfileSetting() {
  const { data: profile, setData: setProfile, franchise } = usePortalData((id) => `/franchise-portal/${id}/profile`, {});
  const [passwordForm, setPasswordForm] = useState({ current_password: "", new_password: "", new_password_confirmation: "" });
  const [savingPassword, setSavingPassword] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState("");
  const [showEdit, setShowEdit] = useState(false);
  const [editForm, setEditForm] = useState({ designation: "", gender: "", dob: "", city: "", pincode: "" });
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileMessage, setProfileMessage] = useState("");

  useEffect(() => {
    setEditForm({
      designation: profile?.designation || "",
      gender: profile?.gender || "",
      dob: profile?.dob || "",
      city: profile?.city || "",
      pincode: profile?.pincode || "",
    });
  }, [profile?.designation, profile?.gender, profile?.dob, profile?.city, profile?.pincode]);

  const profilePhoto = profile?.photo ? fileUrl(profile.photo) : "";
  const profileFields = [
    ["Centre Name", profile?.centre_name || profile?.name],
    ["Login ID", profile?.login_id],
    ["Centre Code", profile?.centre_code],
    ["Application No", profile?.application_no],
    ["Incharge Name", profile?.incharge_name],
    ["State", profile?.state_name],
    ["District", profile?.district_name],
    ["Centre Address", profile?.address],
    ["Course Category", profile?.academy_category],
    ["Mobile", profile?.contact || profile?.incharge_mobile],
    ["Email", profile?.email || profile?.incharge_email],
    ["Designation", profile?.designation],
    ["Gender", profile?.gender],
    ["Date of Birth", profile?.dob],
    ["City", profile?.city],
    ["Pincode", profile?.pincode],
  ];

  const saveProfile = async (event) => {
    event.preventDefault();
    if (!franchise?.id) return;

    try {
      setSavingProfile(true);
      setProfileMessage("");
      const res = await axios.post(`${API}/franchise-portal/${franchise.id}/profile`, editForm);
      setProfile((prev) => ({ ...prev, ...editForm }));
      setProfileMessage(res.data?.message || "Profile updated successfully");
      setShowEdit(false);
    } catch (error) {
      setProfileMessage(getErrorMessage(error, "Profile update failed"));
    } finally {
      setSavingProfile(false);
    }
  };

  const downloadAffiliationCertificate = () => {
    const element = document.createElement("div");
    element.style.padding = "36px";
    element.style.fontFamily = "serif";
    element.style.border = "10px solid #7c2d3a";
    element.innerHTML = `
      <div style="text-align:center;border:2px solid #c2410c;padding:32px;min-height:620px;">
        <h1 style="margin:0;color:#7c2d3a;font-size:30px;">NIDCS</h1>
        <p style="margin:8px 0 24px;color:#64748b;">National Institute of Digital Careers and Skills</p>
        <h2 style="font-size:28px;margin:30px 0;color:#0f172a;">Affiliation Certificate</h2>
        <p style="font-size:17px;line-height:1.8;">This is to certify that</p>
        <h3 style="font-size:26px;color:#c2410c;margin:16px 0;">${profile?.centre_name || "-"}</h3>
        <p style="font-size:17px;line-height:1.8;">is affiliated with NIDCS as an authorized franchise centre.</p>
        <table style="width:100%;border-collapse:collapse;margin-top:28px;text-align:left;">
          <tr><td style="border:1px solid #cbd5e1;padding:10px;">Centre Code</td><td style="border:1px solid #cbd5e1;padding:10px;">${profile?.centre_code || profile?.login_id || "-"}</td></tr>
          <tr><td style="border:1px solid #cbd5e1;padding:10px;">Category</td><td style="border:1px solid #cbd5e1;padding:10px;">${profile?.academy_category || "-"}</td></tr>
          <tr><td style="border:1px solid #cbd5e1;padding:10px;">Location</td><td style="border:1px solid #cbd5e1;padding:10px;">${profile?.state_name || "-"} / ${profile?.district_name || "-"}</td></tr>
        </table>
        <div style="display:flex;justify-content:space-between;margin-top:70px;">
          <div><div style="border-top:1px solid #0f172a;width:180px;"></div><p>Franchise Authority</p></div>
          <div><div style="border-top:1px solid #0f172a;width:180px;"></div><p>NIDCS Administrator</p></div>
        </div>
      </div>
    `;
    html2pdf().set({
      margin: 0.25,
      filename: `affiliation-certificate-${profile?.centre_code || profile?.login_id || "franchise"}.pdf`,
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: "in", format: "a4", orientation: "portrait" },
    }).from(element).save();
  };

  const changePassword = async (event) => {
    event.preventDefault();
    if (!franchise?.id) return;

    try {
      setSavingPassword(true);
      setPasswordMessage("");
      const res = await axios.post(`${API}/franchise-portal/${franchise.id}/change-password`, passwordForm);
      setPasswordMessage(res.data?.message || "Password changed successfully");
      setPasswordForm({ current_password: "", new_password: "", new_password_confirmation: "" });
    } catch (error) {
      setPasswordMessage(getErrorMessage(error, "Password change failed"));
    } finally {
      setSavingPassword(false);
    }
  };

  return (
    <div style={{ display: "grid", gap: "18px" }}>
      <ModuleHeader title="Profile Setting" description="Full franchise profile, affiliation certificate, and password security controls." />
      <div style={{ ...statCard, display: "grid", gridTemplateColumns: "auto 1fr", gap: "18px", alignItems: "center" }}>
        <div style={{ width: "112px", height: "112px", borderRadius: "28px", overflow: "hidden", background: "linear-gradient(135deg, #7c2d3a 0%, #c2410c 100%)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "30px", fontWeight: 800 }}>
          {profilePhoto ? <img src={profilePhoto} alt="Profile" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : (profile?.centre_name || "FR").slice(0, 2).toUpperCase()}
        </div>
        <div style={{ minWidth: 0 }}>
          <h3 style={{ margin: 0, color: "#0f172a", overflowWrap: "anywhere" }}>{profile?.centre_name || "-"}</h3>
          <p style={{ margin: "6px 0 0", color: "#64748b", overflowWrap: "anywhere" }}>{profile?.login_id || "-"}</p>
          <div className="d-flex flex-wrap gap-2 mt-3">
            <button type="button" className="btn btn-primary btn-sm" onClick={() => setShowEdit(true)}>Edit Profile</button>
            <button type="button" className="btn btn-success btn-sm" onClick={downloadAffiliationCertificate}>Download Affiliation Certificate</button>
          </div>
          {profileMessage ? <div className="small fw-bold mt-2" style={{ color: profileMessage.toLowerCase().includes("failed") ? "#b91c1c" : "#166534" }}>{profileMessage}</div> : null}
        </div>
      </div>

      <div style={{ ...statCard }}>
        <h3 style={{ margin: "0 0 14px", color: "#0f172a" }}>Full Profile View</h3>
        <div style={detailGridStyle}>
          {profileFields.map(([label, value]) => (
            <div key={label} style={{ border: "1px solid rgba(148,163,184,0.16)", borderRadius: "14px", padding: "12px", minWidth: 0 }}>
              <div style={{ color: "#64748b", fontSize: "12px", fontWeight: 700 }}>{label}</div>
              <div style={{ color: "#0f172a", fontWeight: 700, marginTop: "5px", overflowWrap: "anywhere" }}>{value || "-"}</div>
            </div>
          ))}
        </div>
      </div>

      {showEdit ? (
        <div style={overlayStyle} onClick={() => setShowEdit(false)}>
          <form style={{ ...modalCardStyle, display: "grid", gap: "12px" }} onSubmit={saveProfile} onClick={(event) => event.stopPropagation()}>
            <h3 style={{ margin: 0 }}>Edit Profile</h3>
            <div className="alert alert-info py-2 mb-0">Centre Name, Incharge Name, State, District, Centre Address, Course Category, Mobile and Email are locked.</div>
            <input className="form-control" placeholder="Designation" value={editForm.designation} onChange={(e) => setEditForm((p) => ({ ...p, designation: e.target.value }))} />
            <select className="form-select" value={editForm.gender} onChange={(e) => setEditForm((p) => ({ ...p, gender: e.target.value }))}>
              <option value="">Select Gender</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
              <option value="Other">Other</option>
            </select>
            <input type="date" className="form-control" value={editForm.dob || ""} onChange={(e) => setEditForm((p) => ({ ...p, dob: e.target.value }))} />
            <input className="form-control" placeholder="City" value={editForm.city} onChange={(e) => setEditForm((p) => ({ ...p, city: e.target.value }))} />
            <input className="form-control" placeholder="Pincode" value={editForm.pincode} onChange={(e) => setEditForm((p) => ({ ...p, pincode: e.target.value }))} />
            <div className="d-flex gap-2 justify-content-end">
              <button type="button" className="btn btn-light" onClick={() => setShowEdit(false)}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={savingProfile}>{savingProfile ? "Saving..." : "Save Profile"}</button>
            </div>
          </form>
        </div>
      ) : null}

      <form onSubmit={changePassword} style={{ ...statCard, display: "grid", gap: "12px" }}>
        <div>
          <h3 style={{ margin: 0, color: "#0f172a" }}>Change Password</h3>
          <p style={{ margin: "6px 0 0", color: "#64748b" }}>Current password diye new password save korun.</p>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "12px" }}>
          <input
            type="password"
            className="form-control"
            placeholder="Current password"
            value={passwordForm.current_password}
            onChange={(event) => setPasswordForm((prev) => ({ ...prev, current_password: event.target.value }))}
            required
          />
          <input
            type="password"
            className="form-control"
            placeholder="New password"
            value={passwordForm.new_password}
            onChange={(event) => setPasswordForm((prev) => ({ ...prev, new_password: event.target.value }))}
            required
            minLength={4}
          />
          <input
            type="password"
            className="form-control"
            placeholder="Confirm new password"
            value={passwordForm.new_password_confirmation}
            onChange={(event) => setPasswordForm((prev) => ({ ...prev, new_password_confirmation: event.target.value }))}
            required
            minLength={4}
          />
        </div>
        <div style={{ display: "flex", gap: "12px", alignItems: "center", flexWrap: "wrap" }}>
          <button type="submit" className="btn btn-primary" disabled={savingPassword}>
            {savingPassword ? "Saving..." : "Save Password"}
          </button>
          {passwordMessage ? (
            <span style={{ color: passwordMessage.toLowerCase().includes("success") ? "#166534" : "#b91c1c", fontWeight: 700 }}>
              {passwordMessage}
            </span>
          ) : null}
        </div>
      </form>
    </div>
  );
}

export function NewMessage() {
  const { franchise } = useOutletContext();
  const actor = {
    type: "franchise",
    id: franchise?.id,
    name: franchise?.centre_name || franchise?.login_id || "Franchise",
  };

  return (
    <div>
      <DirectChat actor={actor} title="New Message" />
    </div>
  );
}

export function ViewMessage() {
  const { data: messages } = usePortalData((id) => `/franchise-portal/${id}/messages`, []);
  return (
    <div>
      <ModuleHeader title="View Message" description="All messages attached to this franchise account." />
      <div style={tableWrap}><table style={tableStyle}><thead><tr><th style={thStyle}>Type</th><th style={thStyle}>Subject</th><th style={thStyle}>Status</th></tr></thead><tbody>
        {messages.map((row) => <tr key={row.id}><td style={tdStyle}>{row.message_type}</td><td style={tdStyle}>{row.subject || '-'}</td><td style={tdStyle}>{row.request_status || '-'}</td></tr>)}
      </tbody></table></div>
    </div>
  );
}

export function MessageRequest() {
  const { data: messages } = usePortalData((id) => `/franchise-portal/${id}/messages`, []);
  const requests = messages.filter((row) => row.message_type === 'message_request');
  return (
    <div>
      <ModuleHeader title="Message Request" description="Requests raised from this franchise account are shown here." />
      <div style={tableWrap}><table style={tableStyle}><thead><tr><th style={thStyle}>Subject</th><th style={thStyle}>Request Status</th><th style={thStyle}>Created</th></tr></thead><tbody>
        {requests.map((row) => <tr key={row.id}><td style={tdStyle}>{row.subject || '-'}</td><td style={tdStyle}>{row.request_status || '-'}</td><td style={tdStyle}>{row.created_at || '-'}</td></tr>)}
      </tbody></table></div>
    </div>
  );
}
